import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import '@/styles/TemplateDesigner.scss';
import IconPicker from '@/components/Common/IconPicker';
import {
    FaCog, FaColumns, FaExclamationCircle, FaEye, FaGripHorizontal, FaGripVertical, 
    FaHandPointer, FaIdCard, FaLayerGroup, FaPalette, FaPencilRuler, FaPlus, FaSave, 
    FaSlidersH, FaSpinner, FaThList, FaTimes, FaToolbox, FaTrash, FaPencilAlt, FaCopy,
    FaUndo, FaRedo, FaDownload, FaUpload, FaChevronUp, FaChevronDown,
    FaEquals, FaRulerHorizontal, FaFilter, FaEyeSlash,
    FaQuestionCircle, FaFont, FaList, FaCalendarAlt, FaThLarge, FaTasks, FaMagic,
    FaSignature, FaPen,
    FaBuilding, FaStore, FaUserTie, FaDatabase,
    FaChevronLeft, FaChevronRight, FaAsterisk, FaCubes, FaBolt,
    FaCheckSquare, FaCalendar, FaMinus, FaEnvelope, FaInfoCircle, FaHashtag,
    FaPhone, FaDotCircle, FaStar, FaHeading, FaArrowsAltV, FaAlignLeft,
    FaParagraph, FaClock, FaToggleOn, FaLink, FaSearch, FaBars,
    FaCommentDots, FaIcons,
    FaCheckDouble, FaThLarge as FaThLargeIcon, FaImage, FaExclamationTriangle, FaBalanceScale
} from 'react-icons/fa';
import { hasInlineLabel, isDisplayOnlyField } from '@/utils/fieldTypeUtils';
import * as kanbanBoardService from '@/services/kanbanBoardService'; 
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';
import WorkflowDesigner from '@/components/ActionItems/WorkflowDesigner';

/**
 * Convert a display name into a safe, URL-friendly template key.
 * e.g. "Asset Service Action Item" → "Asset-Service-Action-Item"
 */
const slugifyKey = (name) =>
    (name || '').trim()
        .replace(/[^a-zA-Z0-9\s-_]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '');


/**
 * Available field types for the template designer.
 * Each field type has properties that define its behavior and appearance.
 * Note: These are sorted alphabetically by label for easier navigation.
 */
const FIELD_TYPES = [
    { 
        type: 'checkbox', 
        label: 'Checkbox', 
        icon: FaCheckSquare, 
        description: 'Boolean yes/no checkbox field',
        example: 'Example: "I agree to terms and conditions"',
        category: 'selection' 
    },
    { 
        type: 'date', 
        label: 'Date', 
        icon: FaCalendar, 
        description: 'Date picker field for selecting a specific date',
        example: 'Example: "Inspection date", "Due date"',
        category: 'datetime' 
    },
    { 
        type: 'datetime', 
        label: 'Date & Time', 
        icon: FaCalendarAlt, 
        description: 'Combined date and time picker',
        example: 'Example: "Appointment time", "Event start"',
        category: 'datetime' 
    },
    { 
        type: 'divider', 
        label: 'Divider', 
        icon: FaMinus, 
        description: 'Horizontal line to visually separate sections',
        example: 'Example: Use between major form sections',
        category: 'layout' 
    },
    { 
        type: 'select', 
        label: 'Dropdown', 
        icon: FaList, 
        description: 'Dropdown menu for selecting one option from a list',
        example: 'Example: "Select department", "Choose priority level"',
        category: 'selection' 
    },
    { 
        type: 'email', 
        label: 'Email', 
        icon: FaEnvelope, 
        description: 'Email address field with validation',
        example: 'Example: "Contact email", "Tenant email address"',
        category: 'basic' 
    },
    { 
        type: 'file', 
        label: 'File Upload', 
        icon: FaUpload, 
        description: 'File attachment field for uploading documents',
        example: 'Example: "Upload invoice", "Attach inspection report"',
        category: 'advanced' 
    },
    { 
        type: 'paragraph', 
        label: 'Info Text', 
        icon: FaInfoCircle, 
        description: 'Read-only informational text or instructions',
        example: 'Example: "Please complete all required fields before submitting"',
        category: 'layout' 
    },
    { 
        type: 'row', 
        label: 'Row', 
        icon: FaColumns, 
        description: 'Bootstrap-style row container with columns for side-by-side fields',
        example: 'Example: Place "First Name" and "Last Name" in separate columns',
        category: 'layout' 
    },
    { 
        type: 'number', 
        label: 'Number', 
        icon: FaHashtag, 
        description: 'Numeric input field with validation',
        example: 'Example: "Unit number", "Amount", "Quantity"',
        category: 'basic' 
    },
    { 
        type: 'phone', 
        label: 'Phone', 
        icon: FaPhone, 
        description: 'Phone number field with formatting',
        example: 'Example: "Contact number", "Emergency phone"',
        category: 'basic' 
    },
    { 
        type: 'radio', 
        label: 'Radio Group', 
        icon: FaDotCircle, 
        description: 'Single selection from multiple visible options',
        example: 'Example: "Property type: Residential/Commercial/Industrial"',
        category: 'selection' 
    },
    { 
        type: 'rating', 
        label: 'Rating', 
        icon: FaStar, 
        description: 'Star rating field (1-5 stars)',
        example: 'Example: "Rate service quality", "Tenant satisfaction"',
        category: 'advanced' 
    },
    { 
        type: 'heading', 
        label: 'Section Header', 
        icon: FaHeading, 
        description: 'Section title or heading text',
        example: 'Example: "Property Details", "Contact Information"',
        category: 'layout' 
    },
    { 
        type: 'signature', 
        label: 'Signature', 
        icon: FaSignature, 
        description: 'Digital signature capture field',
        example: 'Example: "Tenant signature", "Inspector signature"',
        category: 'advanced' 
    },
    { 
        type: 'slider', 
        label: 'Slider', 
        icon: FaSlidersH, 
        description: 'Range slider for selecting a value within a range',
        example: 'Example: "Set priority level 1-10", "Rate condition 0-100"',
        category: 'advanced' 
    },
    { 
        type: 'spacer', 
        label: 'Spacer', 
        icon: FaArrowsAltV, 
        description: 'Vertical spacing element to add blank space',
        example: 'Example: Add space between form sections for better readability',
        category: 'layout' 
    },
    { 
        type: 'textarea', 
        label: 'Text Area', 
        icon: FaAlignLeft, 
        description: 'Multi-line text input for longer free-form content that may span several sentences or paragraphs',
        example: 'Example: "Description", "Comments", "Notes", "Reason for visit"',
        category: 'basic' 
    },
    { 
        type: 'text', 
        label: 'Text Input', 
        icon: FaFont, 
        description: 'Single-line text input for short answers such as names, codes, or identifiers (not suitable for multi-line content)',
        example: 'Example: "Name", "Address", "Property code", "Reference number"',
        category: 'basic' 
    },
    {
        type: 'markdown-block',
        label: 'Markdown Text Block',
        icon: FaParagraph,
        description: 'Static read-only block of formatted text written in Markdown. Use to add instructions, context, or formatted notes directly inside the form without creating an input field.',
        example: 'Example: "**Important:** Please attach supporting documents before submitting."',
        category: 'layout'
    },
    { 
        type: 'time', 
        label: 'Time', 
        icon: FaClock, 
        description: 'Time picker field for selecting a specific time',
        example: 'Example: "Start time", "Meeting time"',
        category: 'datetime' 
    },
    { 
        type: 'toggle', 
        label: 'Toggle Switch', 
        icon: FaToggleOn, 
        description: 'Yes/No toggle switch',
        example: 'Example: "Active", "Enabled", "Allow access"',
        category: 'selection' 
    },
    { 
        type: 'url', 
        label: 'URL', 
        icon: FaLink, 
        description: 'Website URL field with validation',
        example: 'Example: "Property website", "Reference link"',
        category: 'basic' 
    },
    {
        type: 'property-selector',
        label: 'Property Selector',
        icon: FaBuilding,
        description: 'Dropdown that loads properties the current user has access to (permission-filtered)',
        example: 'Example: "Select Property" — only shows properties the user can access',
        category: 'data-linked'
    },
    {
        type: 'tenant-selector',
        label: 'Tenant Selector',
        icon: FaUserTie,
        description: 'Searchable tenant dropdown filtered by the selected property. Requires a Property Selector field.',
        example: 'Example: "Select Tenant" — searches MDA tenants for the chosen property',
        category: 'data-linked'
    },
    {
        type: 'shop-number',
        label: 'Shop Number',
        icon: FaStore,
        description: 'Shop/unit number selector filtered by the selected property. Requires a Property Selector field.',
        example: 'Example: "Shop Number" — lists rentable units for the chosen property',
        category: 'data-linked'
    },
    {
        type: 'entra-user-selector',
        label: 'Entra ID User Selector',
        icon: FaUserTie,
        description: 'Searchable Microsoft 365 / Entra ID user lookup. Auto-populates linked fields (job title, department, manager, device hostname) when a user is selected.',
        example: 'Example: "Employee Name" for onboarding/offboarding — auto-fills department, job title, line manager, and computer hostname',
        category: 'data-linked'
    },
    {
        type: 'role-selector',
        label: 'CapTrac Role Selector',
        icon: FaUserTie,
        description: 'Dropdown populated with CapTrac application roles. Useful for workflow automations that need to target users in a specific role.',
        example: 'Example: "Select Role" — picks a role like "Human Resources" for email cleanup automations',
        category: 'data-linked'
    },
    {
        type: 'question-block',
        label: 'Question / Text Block',
        icon: FaCommentDots,
        description: 'A styled text block for asking questions, giving instructions, or adding contextual notes. Choose from question, instruction, warning, or note styles.',
        example: 'Example: "What is the current condition of the parking lot surface?"',
        category: 'layout'
    },
    {
        type: 'icon-display',
        label: 'Icon',
        icon: FaIcons,
        description: 'Decorative icon element displayed on the form. Select a FontAwesome icon, size, and color to add visual cues or section markers.',
        example: 'Example: A large fire extinguisher icon next to a safety section',
        category: 'layout'
    },
    {
        type: 'multi-select',
        label: 'Multi-Select',
        icon: FaCheckDouble,
        description: 'Checkbox list allowing users to select multiple options from a predefined set.',
        example: 'Example: "Select all applicable issues: Plumbing, Electrical, HVAC, Structural"',
        category: 'selection'
    },
    {
        type: 'likert-scale',
        label: 'Likert Scale',
        icon: FaBalanceScale,
        description: 'Agreement / satisfaction scale (e.g. Strongly Disagree → Strongly Agree) for survey questions.',
        example: 'Example: "Rate your satisfaction with the maintenance response time"',
        category: 'advanced'
    },
    {
        type: 'image-display',
        label: 'Image',
        icon: FaImage,
        description: 'Static image block displayed on the form. Provide a URL to show a reference photo, diagram, or logo.',
        example: 'Example: A floor plan image above an inspection checklist section',
        category: 'layout'
    },
    {
        type: 'info-alert',
        label: 'Alert Banner',
        icon: FaExclamationTriangle,
        description: 'Prominent alert banner to highlight critical instructions, warnings, or notices.',
        example: 'Example: "⚠️ This inspection must be completed before the tenant moves in."',
        category: 'layout'
    },
    {
        type: 'lookup-selector',
        label: 'Lookup Selector',
        icon: FaSearch,
        description: 'Generic API-backed dropdown or search field. Configure the endpoint, value, and display mappings to pull data from any CapTrac API.',
        example: 'Example: "Select Vendor" backed by /api/vendors, or "Choose Asset" from /api/assets',
        category: 'data-linked'
    },
    {
        type: 'repeater',
        label: 'Repeater',
        icon: FaCubes,
        description: 'Dynamic list of repeating field groups. Users can add or remove rows of sub-fields at runtime.',
        example: 'Example: "Line Items" — each row has Description, Qty, Unit Price',
        category: 'advanced'
    },
    {
        type: 'computed-value',
        label: 'Computed Value',
        icon: FaBolt,
        description: 'Read-only field whose value is calculated from other fields using a simple expression.',
        example: 'Example: Total = Qty × Unit Price, or Full Name = First + " " + Last',
        category: 'advanced'
    }
];

/**
 * Default priority options for action items.
 */
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

/**
 * Default status options for action items.
 */
const STATUS_OPTIONS = ['Open', 'On-hold', 'Pending', 'In Progress', 'Solved', 'Cancelled', 'Closed'];

/**
 * Generates a unique identifier for fields and sections.
 * @returns {string} A unique ID string
 */
const generateId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * SortableField Component
 * Renders a draggable field item in the template canvas.
 * Supports drag-and-drop reordering, selection for editing, and duplication.
 * Note: Drag functionality is applied only to the drag handle to allow
 * proper interaction with other field elements (remove button, duplicate button).
 */
const SortableField = ({ field, sectionId, isSelected, onSelect, onRemove, onDuplicate, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) => {
    // useSortable hook provides drag-and-drop functionality for fields
    // data attribute identifies this as a field for drag type detection
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ 
        id: field.id,
        data: {
            type: 'field',
            field,
            sectionId
        }
    });

    // Style transformation for drag-and-drop visual feedback
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 100 : 'auto'
    };

    // Find the field type definition for displaying appropriate icon and label
    const fieldTypeDef = FIELD_TYPES.find(ft => ft.type === field.type) || FIELD_TYPES[0];
    // Resolve category info defensively:
    // 1. Try the specific category on FIELD_CATEGORIES if available.
    // 2. Fall back to the "basic" category if it exists.
    // 3. As a final safeguard, use a local generic category definition so the UI never crashes.
    const categoryInfo =
        FIELD_CATEGORIES?.[fieldTypeDef.category] ||
        FIELD_CATEGORIES?.basic ||
        {
            label: 'Unknown',
            icon: FaQuestionCircle,
            color: '#999999'
        };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`designer-field ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                onSelect(field.id);
            }}
            {...attributes}
            data-field-id={field.id}
        >
            {/* Drag handle - only this element triggers dragging */}
            <div 
                className="field-drag-handle" 
                {...listeners}
                style={{ touchAction: 'none', cursor: isDragging ? 'grabbing' : 'grab' }}
                title="Drag field"
                aria-label="Drag field"
            >
                <FaGripVertical />
            </div>

            {/* Field content */}
            <div className="field-content">
                <div className="field-header">
                    <span className="field-label">
                        {field.label || ''}
                        {field.required && <span className="required-badge">*</span>}
                    </span>
                    {field.conditions?.length > 0 && (
                        <span className="conditional-badge" title={`${field.conditions.length} condition(s)`}>
                            <FaFilter />
                        </span>
                    )}
                </div>
                <div className="field-meta">
                    <span className="field-type-badge" style={{ background: `${categoryInfo.color}15`, borderColor: `${categoryInfo.color}40`, color: categoryInfo.color }}>
                        {fieldTypeDef.label}
                    </span>
                    <span className="field-name">{field.name}</span>
                </div>
            </div>

            {/* Action buttons */}
            <div className="field-actions">
                {/* Move up/down buttons */}
                {onMoveUp && canMoveUp && (
                    <button
                        type="button"
                        className="field-action-btn move-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoveUp(field.id);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        title="Move up"
                        aria-label="Move field up"
                    >
                        <FaChevronUp />
                    </button>
                )}
                {onMoveDown && canMoveDown && (
                    <button
                        type="button"
                        className="field-action-btn move-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMoveDown(field.id);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        title="Move down"
                        aria-label="Move field down"
                    >
                        <FaChevronDown />
                    </button>
                )}
                {onDuplicate && (
                    <button
                        type="button"
                        className="field-action-btn field-duplicate-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDuplicate(field.id);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        title="Duplicate field"
                        aria-label="Duplicate field"
                    >
                        <FaCopy />
                    </button>
                )}
                <button
                    type="button"
                    className="field-action-btn field-remove-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(field.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Remove field"
                    aria-label="Remove field"
                >
                    <FaTimes />
                </button>
            </div>
        </div>
    );
};

SortableField.propTypes = {
    field: PropTypes.object.isRequired,
    sectionId: PropTypes.string.isRequired,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onDuplicate: PropTypes.func,
    onMoveUp: PropTypes.func,
    onMoveDown: PropTypes.func,
    canMoveUp: PropTypes.bool,
    canMoveDown: PropTypes.bool
};


/**
 * DroppableColumn Component
 * Individual column within a row that can receive dropped fields from the sidebar.
 * WordPress-style drop zone with visual feedback.
 * Enhanced to support field selection and editing.
 */
const DroppableColumn = ({ 
    column, 
    rowId, 
    columns, 
    handleUpdateColumnSpan, 
    handleRemoveColumn, 
    handleRemoveFieldFromColumn,
    onAddFieldToColumn,
    selectedFieldId,
    onSelectField
}) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const { setNodeRef, isOver } = useDroppable({
        id: `row_${rowId}_col_${column.id}`,
        data: { type: 'column', rowId, columnId: column.id }
    });

    const isEmpty = !column.fields || column.fields.length === 0;

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const fieldType = e.dataTransfer.getData('fieldType');
        if (fieldType && onAddFieldToColumn) {
            onAddFieldToColumn(column.id, fieldType);
        }
    };

    return (
        <div 
            ref={setNodeRef}
            key={column.id}
            className={`row-column ${isOver || isDragOver ? 'drag-over' : ''} ${isEmpty ? 'row-column-empty' : ''}`}
            style={{ 
                flex: `1 1 ${(column.span / 12) * 100}%`,
                maxWidth: `calc(${(column.span / 12) * 100}% - 4px)`
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div className="column-header">
                <span className="column-label">col-{column.span}</span>
                <select
                    className="column-span-select"
                    value={column.span}
                    onChange={(e) => {
                        e.stopPropagation();
                        handleUpdateColumnSpan(column.id, e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    title="Column width"
                >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                        <option key={n} value={n}>{n}/12</option>
                    ))}
                </select>
                {columns.length > 1 && (
                    <button
                        type="button"
                        className="remove-column-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveColumn(column.id);
                        }}
                        title="Remove column"
                        aria-label="Remove column"
                    >
                        <FaTimes />
                    </button>
                )}
            </div>

            {/* Fields in column or empty state */}
            <div className="column-fields">
                {isEmpty ? (
                    <div className="empty-message">
                        <FaHandPointer />
                        <span>Drag fields here</span>
                    </div>
                ) : (
                    column.fields.map((colField) => {
                        const fieldTypeDef = FIELD_TYPES.find(ft => ft.type === colField.type) || FIELD_TYPES[0];
                        const isSelected = selectedFieldId === colField.id;
                        const categoryInfo = FIELD_CATEGORIES[fieldTypeDef.category] || FIELD_CATEGORIES.basic;
                        
                        return (
                            <div 
                                key={colField.id} 
                                className={`column-field-item ${isSelected ? 'selected' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onSelectField) {
                                        onSelectField(colField.id);
                                    }
                                }}
                                title="Click to edit field properties"
                            >
                                <i className={`fas ${fieldTypeDef.icon}`} style={{ color: categoryInfo.color }}></i>
                                <div className="column-field-info">
                                    <span className="column-field-label">{colField.label || fieldTypeDef.label}</span>
                                    {colField.placeholder && (
                                        <span className="column-field-placeholder">"{colField.placeholder}"</span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="remove-field-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveFieldFromColumn(column.id, colField.id);
                                    }}
                                    title="Remove field"
                                    aria-label="Remove field"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

DroppableColumn.propTypes = {
    column: PropTypes.shape({
        id: PropTypes.string.isRequired,
        span: PropTypes.number.isRequired,
        fields: PropTypes.array
    }).isRequired,
    rowId: PropTypes.string.isRequired,
    columns: PropTypes.array.isRequired,
    handleUpdateColumnSpan: PropTypes.func.isRequired,
    handleRemoveColumn: PropTypes.func.isRequired,
    handleRemoveFieldFromColumn: PropTypes.func.isRequired,
    onAddFieldToColumn: PropTypes.func,
    selectedFieldId: PropTypes.string,
    onSelectField: PropTypes.func
};

/**
 * ColumnResizer Component
 * Draggable handle between columns that allows resizing column widths.
 * Uses mouse events to calculate new span values based on drag distance.
 */
const ColumnResizer = ({ leftColumnId, rightColumnId, leftSpan, rightSpan, onResize }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const resizerRef = React.useRef(null);

    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setStartX(e.clientX);
    };

    React.useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            const parentWidth = resizerRef.current?.parentElement?.offsetWidth || 800;
            const deltaX = e.clientX - startX;
            const spanChange = Math.round((deltaX / parentWidth) * 12);
            
            if (spanChange !== 0) {
                const newLeftSpan = Math.max(1, Math.min(11, leftSpan + spanChange));
                const newRightSpan = Math.max(1, Math.min(11, rightSpan - spanChange));
                
                // Only update if both columns have valid spans
                if (newLeftSpan + newRightSpan === leftSpan + rightSpan && newLeftSpan >= 1 && newRightSpan >= 1) {
                    onResize(leftColumnId, rightColumnId, newLeftSpan, newRightSpan);
                    setStartX(e.clientX);
                }
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, startX, leftColumnId, rightColumnId, leftSpan, rightSpan, onResize]);

    return (
        <div
            ref={resizerRef}
            className={`column-resizer ${isDragging ? 'dragging' : ''}`}
            onMouseDown={handleMouseDown}
            title="Drag to resize columns"
        >
            <div className="resizer-handle">
                <FaGripVertical />
            </div>
        </div>
    );
};

ColumnResizer.propTypes = {
    leftColumnId: PropTypes.string.isRequired,
    rightColumnId: PropTypes.string.isRequired,
    leftSpan: PropTypes.number.isRequired,
    rightSpan: PropTypes.number.isRequired,
    onResize: PropTypes.func.isRequired
};

/**
 * Preset column layouts for quick selection.
 */
const COLUMN_PRESETS = [
    { label: '1 Column', columns: [12], icon: '?' },
    { label: '2 Equal', columns: [6, 6], icon: '??' },
    { label: '3 Equal', columns: [4, 4, 4], icon: '???' },
    { label: '4 Equal', columns: [3, 3, 3, 3], icon: '????' },
    { label: '1/3 + 2/3', columns: [4, 8], icon: '? ??' },
    { label: '2/3 + 1/3', columns: [8, 4], icon: '?? ?' },
    { label: '1/4 + 3/4', columns: [3, 9], icon: '? ???' },
    { label: '1/4 + 1/2 + 1/4', columns: [3, 6, 3], icon: '? ?? ?' },
];

/**
 * SortableRow Component
 * Bootstrap-style row container with configurable columns for horizontal field layout.
 * Supports drag-and-drop of fields into columns with visual gridlines.
 * Enhanced with visual column resizer and preset layouts.
 * Now supports field selection within columns for editing.
 */
const SortableRow = ({ field, sectionId, isSelected, onSelect, onRemove, onUpdate, selectedFieldId, onSelectField }) => {
    const [showPresets, setShowPresets] = useState(false);
    
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ 
        id: field.id,
        data: {
            type: 'row',
            field,
            sectionId
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 100 : 'auto'
    };

    // Initialize columns if not present (default: 2 equal columns)
    const columns = field.columns || [
        { id: generateId(), span: 6, fields: [] },
        { id: generateId(), span: 6, fields: [] }
    ];

    const handleAddColumn = () => {
        const newColumns = [...columns, { id: generateId(), span: 12 / (columns.length + 1), fields: [] }];
        // Redistribute spans evenly
        const evenSpan = Math.floor(12 / newColumns.length);
        newColumns.forEach((col, idx) => {
            col.span = idx === newColumns.length - 1 ? 12 - (evenSpan * (newColumns.length - 1)) : evenSpan;
        });
        onUpdate({ ...field, columns: newColumns });
    };

    const handleRemoveColumn = (columnId) => {
        if (columns.length <= 1) return; // Keep at least one column
        const newColumns = columns.filter(col => col.id !== columnId);
        // Redistribute spans evenly
        const evenSpan = Math.floor(12 / newColumns.length);
        newColumns.forEach((col, idx) => {
            col.span = idx === newColumns.length - 1 ? 12 - (evenSpan * (newColumns.length - 1)) : evenSpan;
        });
        onUpdate({ ...field, columns: newColumns });
    };

    const handleUpdateColumnSpan = (columnId, newSpan) => {
        const newColumns = columns.map(col => 
            col.id === columnId ? { ...col, span: parseInt(newSpan) } : col
        );
        onUpdate({ ...field, columns: newColumns });
    };

    /**
     * Handle resizing two adjacent columns.
     */
    const handleColumnResize = (leftId, rightId, newLeftSpan, newRightSpan) => {
        const newColumns = columns.map(col => {
            if (col.id === leftId) return { ...col, span: newLeftSpan };
            if (col.id === rightId) return { ...col, span: newRightSpan };
            return col;
        });
        onUpdate({ ...field, columns: newColumns });
    };

    /**
     * Distribute column widths evenly.
     */
    const handleDistributeEvenly = () => {
        const evenSpan = Math.floor(12 / columns.length);
        const remainder = 12 - (evenSpan * columns.length);
        const newColumns = columns.map((col, idx) => ({
            ...col,
            span: evenSpan + (idx < remainder ? 1 : 0)
        }));
        onUpdate({ ...field, columns: newColumns });
    };

    /**
     * Apply a preset column layout.
     *
     * This implementation preserves existing column data where possible and
     * additionally protects against data loss when the preset defines fewer
     * columns than the current layout.
     *
     * Previous behavior:
     * - Only columns up to preset.columns.length were preserved.
     * - Any fields in columns beyond that index were silently discarded.
     *
     * Updated behavior:
     * - For indices that exist in both the current layout and the preset,
     *   we preserve the column id and fields while updating the span.
     * - If the current layout has more columns than the preset, all fields
     *   from the "extra" columns are collected and appended to the last
     *   column defined by the preset, ensuring no field data is lost.
     */
    const handleApplyPreset = (preset) => {
        // Defensive guard: if the preset is invalid or has no columns,
        // simply close the preset selector without modifying the layout.
        if (!preset || !Array.isArray(preset.columns) || preset.columns.length === 0) {
            setShowPresets(false);
            return;
        }

        const presetColumnCount = preset.columns.length;

        // Build the new columns based on the preset, preserving existing
        // column ids and fields where indices overlap.
        const newColumns = preset.columns.map((span, idx) => {
            const existingCol = columns[idx];

            return {
                id: existingCol?.id || generateId(),
                span,
                // Clone the fields array if it exists so we do not retain
                // references to the original array and avoid accidental mutation.
                fields: existingCol?.fields ? [...existingCol.fields] : []
            };
        });

        // If the current layout has more columns than the preset, collect
        // all fields from the "extra" columns so they can be merged into
        // the last column of the new layout instead of being dropped.
        if (columns.length > presetColumnCount) {
            const extraFields = columns
                .slice(presetColumnCount)
                .reduce((acc, column) => {
                    if (Array.isArray(column.fields) && column.fields.length > 0) {
                        acc.push(...column.fields);
                    }

                    return acc;
                }, []);

            if (extraFields.length > 0 && newColumns.length > 0) {
                // Log a warning for developers so it is clear that fields
                // from removed columns were automatically merged.
                // This is a non-intrusive "warning" mechanism that avoids
                // introducing new UI state while still surfacing the behavior.
                // If a future UX requires an explicit confirmation dialog,
                // this is the logical place to hook it in.
                // eslint-disable-next-line no-console
                console.warn(
                    'TemplateDesigner: Preset has fewer columns than the current layout. ' +
                    'Fields from removed columns have been moved into the last column of the preset to avoid data loss.'
                );

                const lastIndex = newColumns.length - 1;
                const lastColumn = newColumns[lastIndex];

                newColumns[lastIndex] = {
                    ...lastColumn,
                    fields: [
                        ...(lastColumn.fields || []),
                        ...extraFields
                    ]
                };
            }
        }

        onUpdate({ ...field, columns: newColumns });
        setShowPresets(false);
    };


    const handleAddFieldToColumn = (columnId, fieldType) => {
        const validFieldType = FIELD_TYPES.find(ft => ft.type === fieldType);
        if (!validFieldType) return;

        const newField = {
            id: generateId(),
            type: fieldType,
            name: `field_${Date.now().toString(36)}`,
            label: `${validFieldType.label}`,
            required: false,
            placeholder: '',
            helpText: '',
            validation: {},
            options: ['select', 'radio'].includes(fieldType) ? ['Option 1', 'Option 2', 'Option 3'] : undefined,
            // Question block defaults
            ...(fieldType === 'question-block' && { content: '', variant: 'question', showIcon: true }),
            // Icon display defaults
            ...(fieldType === 'icon-display' && { iconClass: '', iconSize: 'medium', iconColor: '#4a5568', iconAlign: 'left', caption: '' }),
            // Multi-select defaults
            ...(fieldType === 'multi-select' && { options: ['Option 1', 'Option 2', 'Option 3'], layout: 'vertical', allowOther: false }),
            // Likert scale defaults
            ...(fieldType === 'likert-scale' && { scaleLabels: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'], showLabels: true }),
            // Image display defaults
            ...(fieldType === 'image-display' && { imageSrc: '', imageAlt: '', imageMaxWidth: '100%', imageAlign: 'left', imageCaption: '' }),
            // Info alert defaults
            ...(fieldType === 'info-alert' && { content: '', alertVariant: 'info', showAlertIcon: true })
        };

        const newColumns = columns.map(col =>
            col.id === columnId ? { ...col, fields: [...(col.fields || []), newField] } : col
        );
        onUpdate({ ...field, columns: newColumns });
    };

    const handleRemoveFieldFromColumn = (columnId, fieldId) => {
        const newColumns = columns.map(col =>
            col.id === columnId ? { ...col, fields: col.fields.filter(f => f.id !== fieldId) } : col
        );
        onUpdate({ ...field, columns: newColumns });
    };

    // Calculate total span for validation display
    const totalSpan = columns.reduce((sum, col) => sum + col.span, 0);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`designer-row ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                onSelect(field.id);
            }}
            data-field-id={field.id}
        >
            {/* WordPress-style hover actions */}
            <div className="row-hover-actions">
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        // Duplicate row functionality
                        const newRow = {
                            ...field,
                            id: generateId(),
                            columns: field.columns.map(col => ({
                                ...col,
                                id: generateId(),
                                fields: col.fields.map(f => ({ ...f, id: generateId() }))
                            }))
                        };
                        // Would need to pass a duplicate handler from parent
                        console.log('Duplicate row:', newRow);
                    }}
                    title="Duplicate Row"
                    aria-label="Duplicate row"
                >
                    <FaCopy />
                </button>
                <button
                    type="button"
                    className="delete-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(field.id);
                    }}
                    title="Delete Row"
                    aria-label="Delete row"
                >
                    <FaTrash />
                </button>
            </div>

            {/* Drag handle */}
            <div 
                className="row-drag-handle" 
                {...attributes}
                {...listeners}
                style={{ touchAction: 'none', cursor: isDragging ? 'grabbing' : 'grab' }}
                title="Drag row"
                aria-label="Drag row"
            >
                <FaGripVertical />
            </div>

            {/* Row content */}
            <div className="row-content">
                <div className="row-header">
                    <FaColumns className="row-icon" />
                    <span className="row-label">Row ({columns.length} columns)</span>
                    
                    {/* Column width indicator */}
                    <span className={`span-indicator ${totalSpan !== 12 ? 'invalid' : ''}`}>
                        {totalSpan}/12
                    </span>

                    {/* Layout controls */}
                    <div className="row-layout-controls">
                        <button
                            type="button"
                            className="layout-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDistributeEvenly();
                            }}
                            title="Distribute columns evenly"
                            aria-label="Distribute evenly"
                        >
                            <FaEquals />
                        </button>
                        <div className="preset-dropdown">
                            <button
                                type="button"
                                className="layout-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowPresets(!showPresets);
                                }}
                                title="Column layout presets"
                                aria-label="Layout presets"
                            >
                                <FaRulerHorizontal />
                            </button>
                            {showPresets && (
                                <div className="preset-menu" onClick={(e) => e.stopPropagation()}>
                                    <div className="preset-header">Layout Presets</div>
                                    {COLUMN_PRESETS.map((preset, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            className="preset-item"
                                            onClick={() => handleApplyPreset(preset)}
                                        >
                                            <span className="preset-icon">{preset.icon}</span>
                                            <span className="preset-label">{preset.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="add-column-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddColumn();
                            }}
                            title="Add column"
                            aria-label="Add column"
                        >
                            <FaPlus /> Column
                        </button>
                    </div>
                </div>

                {/* Columns with Resizers */}
                <div className="row-columns">
                    {columns.map((column, idx) => (
                        <React.Fragment key={column.id}>
                            <DroppableColumn
                                column={column}
                                rowId={field.id}
                                columns={columns}
                                handleUpdateColumnSpan={handleUpdateColumnSpan}
                                handleRemoveColumn={handleRemoveColumn}
                                handleRemoveFieldFromColumn={handleRemoveFieldFromColumn}
                                onAddFieldToColumn={handleAddFieldToColumn}
                                selectedFieldId={selectedFieldId}
                                onSelectField={onSelectField}
                            />
                            {/* Add resizer between columns */}
                            {idx < columns.length - 1 && (
                                <ColumnResizer
                                    leftColumnId={column.id}
                                    rightColumnId={columns[idx + 1].id}
                                    leftSpan={column.span}
                                    rightSpan={columns[idx + 1].span}
                                    onResize={handleColumnResize}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};

SortableRow.propTypes = {
    field: PropTypes.object.isRequired,
    sectionId: PropTypes.string.isRequired,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    selectedFieldId: PropTypes.string,
    onSelectField: PropTypes.func
};

/**
 * AddFieldToRowModal Component
 * Modal for selecting which field type to add to a row
 * Implements proper accessibility with ARIA attributes and keyboard navigation
 */
/**
 * SortableSection Component
 * Renders a draggable section container that groups fields together.
 * Supports drag-and-drop reordering of sections with drag handle.
 * Note: Drag functionality is applied only to the drag handle to allow proper
 * interaction with other section header elements (rename input, remove button).
 */
const SortableSection = ({ section, sectionIndex, children, isSelected, onSelect, onRemove, onRename, onUpdateColumns }) => {
    // useSortable hook provides drag-and-drop functionality for sections
    // data attribute identifies this as a section for drag type detection
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ 
        id: section.id,
        data: {
            type: 'section',
            section
        }
    });

    // State for inline editing of section title
    const [isEditing, setIsEditing] = useState(false);
    const [titleValue, setTitleValue] = useState(section.title);

    // Update local title value when section.title prop changes
    if (titleValue !== section.title && !isEditing) {
        setTitleValue(section.title);
    }

    // Style transformation for drag-and-drop visual feedback
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto'
    };

    /**
     * Handles saving the section title when editing is complete.
     * Called on blur or Enter key press.
     */
    const handleTitleSave = useCallback(() => {
        // Only save if title has changed and is not empty
        const trimmedTitle = titleValue.trim();
        if (trimmedTitle && trimmedTitle !== section.title) {
            onRename(section.id, trimmedTitle);
        } else if (!trimmedTitle) {
            // Reset to original title if empty
            setTitleValue(section.title);
        }
        setIsEditing(false);
    }, [titleValue, section.id, section.title, onRename]);

    /**
     * Handles key down events on the title input.
     * Enter saves, Escape cancels editing.
     */
    const handleInputKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleTitleSave();
        } else if (e.key === 'Escape') {
            setTitleValue(section.title);
            setIsEditing(false);
        }
    }, [handleTitleSave, section.title]);

    /**
     * Starts editing mode for section title.
     */
    const startEditing = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        setIsEditing(true);
    }, []);

    // Get current column count (default to 1)
    const columnCount = section.columns || 1;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`designer-section ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                onSelect(section.id);
            }}
            data-section-id={section.id}
        >
            {/* Section header - drag listeners only on the drag handle */}
            <div className="section-header">
                {/* Drag handle with listeners - only this element triggers dragging */}
                <div 
                    className="section-drag-handle" 
                    {...attributes} 
                    {...listeners}
                    style={{ touchAction: 'none', cursor: isDragging ? 'grabbing' : 'grab' }}
                    title="Drag section"
                    aria-label="Drag section"
                >
                    <FaGripHorizontal />
                </div>
                
                {/* Section title - editable on double-click or click on edit icon */}
                {isEditing ? (
                    <input
                        type="text"
                        className="section-title-input"
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        onBlur={handleTitleSave}
                        onKeyDown={handleInputKeyDown}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                    />
                ) : (
                    <h4
                        className="section-title"
                        onDoubleClick={startEditing}
                    >
                        {section.title || `Section ${sectionIndex + 1}`}
                        <FaPencilAlt 
                            className="edit-icon" 
                            title="Click to edit section name"
                            onClick={startEditing}
                        />
                    </h4>
                )}

                {/* Column selector - allows changing layout columns */}
                <div className="section-columns-selector" onClick={(e) => e.stopPropagation()}>
                    <label title="Number of columns in this section">
                        <FaColumns />
                        <select
                            value={columnCount}
                            onChange={(e) => {
                                e.stopPropagation();
                                onUpdateColumns(section.id, parseInt(e.target.value));
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                        >
                            <option value={1}>1 Column</option>
                            <option value={2}>2 Columns</option>
                            <option value={3}>3 Columns</option>
                            <option value={4}>4 Columns</option>
                        </select>
                    </label>
                </div>
                
                {/* Remove section button */}
                <button
                    type="button"
                    className="section-remove-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(section.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    title="Remove section"
                    aria-label="Remove section"
                >
                    <FaTrash />
                </button>
            </div>

            {/* Section content (fields) - with column layout support */}
            <div 
                className="section-content" 
                style={{ 
                    display: 'grid', 
                    gridTemplateColumns: `repeat(${columnCount}, 1fr)`, 
                    gap: '0.5rem' 
                }}
            >
                {children}
            </div>
        </div>
    );
};

SortableSection.propTypes = {
    section: PropTypes.object.isRequired,
    sectionIndex: PropTypes.number.isRequired,
    children: PropTypes.node,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onRename: PropTypes.func.isRequired,
    onUpdateColumns: PropTypes.func.isRequired
};


/**
 * Field category definitions for organizing the palette.
 * Uses React Icon components for proper rendering.
 */
const FIELD_CATEGORIES = {
    basic: { label: 'Basic Inputs', icon: FaFont, color: '#4a7cba' },
    selection: { label: 'Selection', icon: FaList, color: '#7c6bac' },
    datetime: { label: 'Date & Time', icon: FaCalendarAlt, color: '#c47b2c' },
    layout: { label: 'Layout', icon: FaThLarge, color: '#3d8f6f' },
    advanced: { label: 'Advanced', icon: FaMagic, color: '#2a7d7d' },
    'data-linked': { label: 'Data-Linked', icon: FaDatabase, color: '#4a5568' }
};

/**
 * FieldPalette Component
 * Displays available field types that can be added to the canvas.
 * Features: Search filtering, collapsible categories, drag-to-add support.
 */
const FieldPalette = ({ onAddField }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [openCategory, setOpenCategory] = useState(null); // accordion: null = all collapsed

    // Group fields by category
    const fieldsByCategory = useMemo(() => {
        const groups = {};
        FIELD_TYPES.forEach(field => {
            const category = field.category || 'basic';
            if (!groups[category]) groups[category] = [];
            groups[category].push(field);
        });
        return groups;
    }, []);

    // Filter fields based on search term
    const filteredFieldsByCategory = useMemo(() => {
        if (!searchTerm.trim()) {
            return fieldsByCategory;
        }

        const term = searchTerm.toLowerCase();
        const filtered = {};

        Object.entries(fieldsByCategory).forEach(([category, fields]) => {
            const matchingFields = fields.filter(field =>
                field.label.toLowerCase().includes(term) ||
                field.type.toLowerCase().includes(term) ||
                field.description.toLowerCase().includes(term)
            );
            if (matchingFields.length > 0) {
                filtered[category] = matchingFields;
            }
        });

        return filtered;
    }, [fieldsByCategory, searchTerm]);

    // Accordion: toggle opens the clicked category and collapses all others
    const toggleCategory = (category) => {
        setOpenCategory(prev => prev === category ? null : category);
    };

    const totalFilteredCount = Object.values(filteredFieldsByCategory).flat().length;


    return (
        <div className="field-palette">
            <div className="palette-header">
                <h3 className="palette-title">
                    <FaCubes /> Field Library
                </h3>
                <span className="field-count">{FIELD_TYPES.length}</span>
            </div>

            {/* Search Input */}
            <div className="palette-search">
                <FaSearch className="search-icon" />
                <input
                    type="text"
                    placeholder="Search fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
                {searchTerm && (
                    <button 
                        type="button"
                        className="clear-search"
                        onClick={() => setSearchTerm('')}
                        aria-label="Clear search"
                    >
                        <FaTimes />
                    </button>
                )}
            </div>

            {/* Search Results Info */}
            {searchTerm && (
                <div className="search-results-info">
                    {totalFilteredCount === 0 
                        ? 'No fields match your search'
                        : `${totalFilteredCount} field${totalFilteredCount !== 1 ? 's' : ''} found`
                    }
                </div>
            )}

            {/* Fields by Category */}
            <div className="palette-categories">
                {Object.entries(FIELD_CATEGORIES).map(([categoryKey, categoryInfo]) => {
                    const fields = filteredFieldsByCategory[categoryKey];
                    if (!fields || fields.length === 0) return null;

                    // Accordion: a category is collapsed unless it's the open one
                    // When searching, expand all matching categories
                    const isCollapsed = searchTerm.trim() ? false : openCategory !== categoryKey;
                    const CategoryIcon = categoryInfo.icon;

                    return (
                        <div key={categoryKey} className={`palette-category ${isCollapsed ? 'collapsed' : ''}`}>
                            <button
                                type="button"
                                className="category-header"
                                onClick={() => toggleCategory(categoryKey)}
                                style={{ '--cat-color': categoryInfo.color }}
                            >
                                <span className="category-icon-wrapper">
                                    <CategoryIcon />
                                </span>
                                <span className="category-label">{categoryInfo.label}</span>
                                <span className="category-count">{fields.length}</span>
                                <FaChevronDown className={`toggle-icon ${isCollapsed ? 'rotated' : ''}`} />
                            </button>

                            {!isCollapsed && (
                                <div className="category-fields">
                                    {fields.map((fieldType) => (
                                        <button
                                            key={fieldType.type}
                                            type="button"
                                            className="palette-field-btn"
                                            onClick={() => onAddField(fieldType.type)}
                                            title={fieldType.description}
                                            draggable="true"
                                            onDragStart={(e) => {
                                                e.dataTransfer.setData('fieldType', fieldType.type);
                                                e.dataTransfer.effectAllowed = 'copy';
                                            }}
                                            style={{ '--cat-color': categoryInfo.color }}
                                        >
                                            <span className="field-icon-wrapper">
                                                <fieldType.icon />
                                            </span>
                                            <span className="field-label">{fieldType.label}</span>
                                            <span className="field-add-indicator">
                                                <FaPlus />
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Minimal footer hint */}
            <div className="palette-footer">
                <span>Drag or click to add</span>
            </div>
        </div>
    );
};

FieldPalette.propTypes = {
    onAddField: PropTypes.func.isRequired
};

/**
 * FieldConfigPanel Component
 * Panel for configuring selected field properties.
 * Enhanced with conditional logic configuration.
 */
const FieldConfigPanel = ({ field, onUpdate, onClose, allFields = [] }) => {
    const [localField, setLocalField] = useState({ ...field });
    const [optionsText, setOptionsText] = useState(
        field.options ? field.options.join('\n') : ''
    );
    const [subItemsText, setSubItemsText] = useState(
        field.subItems ? field.subItems.map(item => item.label).join('\n') : ''
    );


    // Update local state when field prop changes (render phase update pattern)
    if (localField.id !== field.id) {
        setLocalField({ ...field });
        setOptionsText(field.options ? field.options.join('\n') : '');
        setSubItemsText(field.subItems ? field.subItems.map(item => item.label).join('\n') : '');
    }

    /**
     * Handles changes to field properties.
     * @param {string} key - The property key to update
     * @param {any} value - The new value for the property
     */
    const handleChange = (key, value) => {
        const updated = { ...localField, [key]: value };
        setLocalField(updated);
        onUpdate(updated);
    };

    /**
     * Handles changes to validation properties.
     * @param {string} key - The validation property key
     * @param {any} value - The new value
     */
    const handleValidationChange = (key, value) => {
        const validation = { ...(localField.validation || {}), [key]: value };
        handleChange('validation', validation);
    };

    /**
     * Handles changes to select options.
     * @param {string} text - The options as newline-separated text
     */
    const handleOptionsChange = (text) => {
        setOptionsText(text);
        const options = text.split('\n').filter(o => o.trim() !== '');
        handleChange('options', options);
    };

    /**
     * Handles changes to checklist group sub-items.
     * @param {string} text - The sub-items as newline-separated text
     */
    const handleSubItemsChange = (text) => {
        setSubItemsText(text);
        const labels = text.split('\n').filter(l => l.trim() !== '');
        const subItems = labels.map((label, idx) => ({
            name: `sub_${idx}`,
            label: label.trim()
        }));
        handleChange('subItems', subItems);
    };

    const fieldTypeDef = FIELD_TYPES.find(ft => ft.type === localField.type) || FIELD_TYPES[0];

    return (
        <div className="field-config-panel">
            <div className="config-panel-header">
                <h3>
                    {fieldTypeDef.icon && React.createElement(fieldTypeDef.icon)}
                    Field Configuration
                </h3>
                <button
                    type="button"
                    className="close-btn"
                    onClick={onClose}
                    title="Close panel"
                >
                    <FaTimes />
                </button>
            </div>

            <div className="config-panel-body">
                {/* Basic Properties */}
                <div className="config-section">
                    <h4>Basic Properties</h4>

                    <div className="config-field">
                        <label htmlFor="field-label">Label</label>
                        <input
                            id="field-label"
                            type="text"
                            value={localField.label || ''}
                            onChange={(e) => handleChange('label', e.target.value)}
                            placeholder="Enter field label"
                        />
                    </div>

                    {/* Only show placeholder field for types that support it */}
                    {!['checkbox', 'toggle', 'radio', 'rating', 'slider', 'file', 'signature', 'checklist-item', 'checklist-group', 'heading', 'divider', 'paragraph', 'spacer', 'markdown-block', 'question-block', 'icon-display', 'multi-select', 'likert-scale', 'image-display', 'info-alert'].includes(localField.type) && (
                        <div className="config-field">
                            <label htmlFor="field-placeholder">Placeholder</label>
                            <input
                                id="field-placeholder"
                                type="text"
                                value={localField.placeholder || ''}
                                onChange={(e) => handleChange('placeholder', e.target.value)}
                                placeholder="e.g., Enter description here..."
                            />
                            <small>Hint text shown inside empty input fields.</small>
                        </div>
                    )}

                    <div className="config-field">
                        <label htmlFor="field-helpText">Help Text</label>
                        <input
                            id="field-helpText"
                            type="text"
                            value={localField.helpText || ''}
                            onChange={(e) => handleChange('helpText', e.target.value)}
                            placeholder="Helpful guidance for users"
                        />
                    </div>

                    {/* Default Value — supports static text and expression tokens */}
                    {!['heading', 'divider', 'paragraph', 'spacer', 'markdown-block', 'question-block', 'icon-display', 'image-display', 'info-alert', 'row', 'signature', 'file', 'checklist-item', 'checklist-group'].includes(localField.type) && (
                        <div className="config-field">
                            <label htmlFor="field-defaultValue">Default Value</label>
                            <input
                                id="field-defaultValue"
                                type="text"
                                value={localField.defaultValue || ''}
                                onChange={(e) => handleChange('defaultValue', e.target.value)}
                                placeholder="e.g. {{today}} or static text"
                            />
                            <small>
                                Expressions: <code>{'{{today}}'}</code> <code>{'{{now}}'}</code> <code>{'{{currentUser}}'}</code> <code>{'{{currentUserEmail}}'}</code>
                            </small>
                        </div>
                    )}

                    {/* Only show the "required" checkbox for types that accept user input.
                        Layout / display-only types (heading, paragraph, divider, spacer, markdown-block)
                        cannot be required because they don't collect any data. */}
                    {!['heading', 'divider', 'paragraph', 'spacer', 'markdown-block', 'question-block', 'icon-display', 'image-display', 'info-alert', 'row'].includes(localField.type) && (
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.required || false}
                                    onChange={(e) => handleChange('required', e.target.checked)}
                                />
                                Required field
                            </label>
                        </div>
                    )}
                </div>

                {/* Type-specific options */}
                {localField.type === 'select' && (
                    <div className="config-section">
                        <h4>Dropdown Options</h4>
                        <div className="config-field">
                            <label htmlFor="field-options">Options (one per line)</label>
                            <textarea
                                id="field-options"
                                value={optionsText}
                                onChange={(e) => handleOptionsChange(e.target.value)}
                                rows={5}
                                placeholder="Option 1&#10;Option 2&#10;Option 3"
                            />
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-dependsOnField-select">Dynamic Options: Depends On Field</label>
                            <select
                                id="field-dependsOnField-select"
                                value={localField.dependsOnField || ''}
                                onChange={(e) => handleChange('dependsOnField', e.target.value)}
                            >
                                <option value="">-- None (static options) --</option>
                                {allFields
                                    .filter(f => f.id !== localField.id && ['select', 'radio'].includes(f.type))
                                    .map(f => (
                                        <option key={f.id} value={f.name}>{f.label || f.name}</option>
                                    ))
                                }
                            </select>
                            <small>When set, options change based on the selected value of the source field.</small>
                        </div>
                        {localField.dependsOnField && (
                            <div className="config-field">
                                <label>Option Groups (JSON)</label>
                                <textarea
                                    rows={6}
                                    value={JSON.stringify(localField.optionGroups || {}, null, 2)}
                                    onChange={(e) => {
                                        try {
                                            const parsed = JSON.parse(e.target.value);
                                            handleChange('optionGroups', parsed);
                                        } catch {
                                            // Let user keep typing until valid JSON
                                        }
                                    }}
                                    placeholder={'{\n  "Category A": ["Sub A1", "Sub A2"],\n  "Category B": ["Sub B1", "Sub B2"]\n}'}
                                />
                                <small>Map source field values to option arrays.</small>
                            </div>
                        )}
                    </div>
                )}

                {/* Radio group options */}
                {localField.type === 'radio' && (
                    <div className="config-section">
                        <h4>Radio Options</h4>
                        <div className="config-field">
                            <label htmlFor="field-radio-options">Options (one per line)</label>
                            <textarea
                                id="field-radio-options"
                                value={optionsText}
                                onChange={(e) => handleOptionsChange(e.target.value)}
                                rows={5}
                                placeholder="Option 1&#10;Option 2&#10;Option 3"
                            />
                        </div>
                    </div>
                )}

                {/* Checklist Group sub-items */}
                {localField.type === 'checklist-group' && (
                    <div className="config-section">
                        <h4>Checklist Sub-Items</h4>
                        <p className="config-help-text">
                            Add the items that will appear as checkable sub-items in this group.
                            Each line becomes a separate checkbox item.
                        </p>
                        <div className="config-field">
                            <label htmlFor="field-subitems">Sub-Items (one per line)</label>
                            <textarea
                                id="field-subitems"
                                value={subItemsText}
                                onChange={(e) => handleSubItemsChange(e.target.value)}
                                rows={6}
                                placeholder="perimeter fence&#10;generators&#10;roof &amp; cat ladders&#10;back yards&#10;tenant opening on time"
                            />
                        </div>
                    </div>
                )}

                {/* Validation options for text fields */}
                {['text', 'textarea'].includes(localField.type) && (
                    <div className="config-section">
                        <h4>Validation</h4>
                        <div className="config-field-row">
                            <div className="config-field half">
                                <label htmlFor="field-minLength">Min Length</label>
                                <input
                                    id="field-minLength"
                                    type="number"
                                    value={localField.validation?.minLength || ''}
                                    onChange={(e) => handleValidationChange('minLength', e.target.value ? parseInt(e.target.value) : undefined)}
                                    min="0"
                                />
                            </div>
                            <div className="config-field half">
                                <label htmlFor="field-maxLength">Max Length</label>
                                <input
                                    id="field-maxLength"
                                    type="number"
                                    value={localField.validation?.maxLength || ''}
                                    onChange={(e) => handleValidationChange('maxLength', e.target.value ? parseInt(e.target.value) : undefined)}
                                    min="0"
                                />
                            </div>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-pattern">Pattern (RegEx)</label>
                            <input
                                id="field-pattern"
                                type="text"
                                value={localField.validation?.pattern || ''}
                                onChange={(e) => handleValidationChange('pattern', e.target.value)}
                                placeholder="^[A-Za-z]+$"
                            />
                        </div>
                    </div>
                )}

                {/* Validation options for number fields */}
                {localField.type === 'number' && (
                    <div className="config-section">
                        <h4>Validation</h4>
                        <div className="config-field-row">
                            <div className="config-field half">
                                <label htmlFor="field-min">Minimum Value</label>
                                <input
                                    id="field-min"
                                    type="number"
                                    value={localField.validation?.min ?? ''}
                                    onChange={(e) => handleValidationChange('min', e.target.value !== '' ? parseFloat(e.target.value) : undefined)}
                                />
                            </div>
                            <div className="config-field half">
                                <label htmlFor="field-max">Maximum Value</label>
                                <input
                                    id="field-max"
                                    type="number"
                                    value={localField.validation?.max ?? ''}
                                    onChange={(e) => handleValidationChange('max', e.target.value !== '' ? parseFloat(e.target.value) : undefined)}
                                />
                            </div>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-step">Step</label>
                            <input
                                id="field-step"
                                type="number"
                                value={localField.validation?.step || 1}
                                onChange={(e) => handleValidationChange('step', parseFloat(e.target.value) || 1)}
                                step="any"
                            />
                        </div>
                    </div>
                )}

                {/* Textarea specific options */}
                {localField.type === 'textarea' && (
                    <div className="config-section">
                        <h4>Display</h4>
                        <div className="config-field">
                            <label htmlFor="field-rows">Rows</label>
                            <input
                                id="field-rows"
                                type="number"
                                value={localField.rows || 4}
                                onChange={(e) => handleChange('rows', parseInt(e.target.value) || 4)}
                                min="2"
                                max="20"
                            />
                        </div>
                    </div>
                )}

                {/* File field specific options */}
                {localField.type === 'file' && (
                    <div className="config-section">
                        <h4>File Options</h4>
                        <div className="config-field">
                            <label htmlFor="field-accept">Accepted File Types</label>
                            <input
                                id="field-accept"
                                type="text"
                                value={localField.accept || ''}
                                onChange={(e) => handleChange('accept', e.target.value)}
                                placeholder=".pdf,.doc,.docx,.jpg,.png"
                            />
                        </div>
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.multiple || false}
                                    onChange={(e) => handleChange('multiple', e.target.checked)}
                                />
                                Allow multiple files
                            </label>
                        </div>
                    </div>
                )}

                {/* Rating field options */}
                {localField.type === 'rating' && (
                    <div className="config-section">
                        <h4>Rating Options</h4>
                        <div className="config-field">
                            <label htmlFor="field-maxRating">Maximum Stars</label>
                            <input
                                id="field-maxRating"
                                type="number"
                                value={localField.maxRating || 5}
                                onChange={(e) => handleChange('maxRating', parseInt(e.target.value) || 5)}
                                min="1"
                                max="10"
                            />
                        </div>
                    </div>
                )}

                {/* Slider field options */}
                {localField.type === 'slider' && (
                    <div className="config-section">
                        <h4>Slider Options</h4>
                        <div className="config-field-row">
                            <div className="config-field half">
                                <label htmlFor="field-slider-min">Minimum Value</label>
                                <input
                                    id="field-slider-min"
                                    type="number"
                                    value={localField.validation?.min ?? 0}
                                    onChange={(e) => handleValidationChange('min', parseFloat(e.target.value) || 0)}
                                />
                            </div>
                            <div className="config-field half">
                                <label htmlFor="field-slider-max">Maximum Value</label>
                                <input
                                    id="field-slider-max"
                                    type="number"
                                    value={localField.validation?.max ?? 100}
                                    onChange={(e) => handleValidationChange('max', parseFloat(e.target.value) || 100)}
                                />
                            </div>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-slider-step">Step</label>
                            <input
                                id="field-slider-step"
                                type="number"
                                value={localField.validation?.step || 1}
                                onChange={(e) => handleValidationChange('step', parseFloat(e.target.value) || 1)}
                                step="any"
                            />
                        </div>
                    </div>
                )}

                {/* Layout fields - content configuration */}
                {['heading', 'paragraph'].includes(localField.type) && (
                    <div className="config-section">
                        <h4>Content</h4>
                        <div className="config-field">
                            <label htmlFor="field-content">
                                {localField.type === 'heading' ? 'Header Text' : 'Paragraph Text'}
                            </label>
                            <textarea
                                id="field-content"
                                value={localField.content || ''}
                                onChange={(e) => handleChange('content', e.target.value)}
                                rows={localField.type === 'heading' ? 2 : 4}
                                placeholder={localField.type === 'heading' ? 'Section Title' : 'Informational text...'}
                            />
                        </div>
                    </div>
                )}

                {/* Markdown Text Block - editable markdown content displayed read-only in the form */}
                {localField.type === 'markdown-block' && (
                    <div className="config-section">
                        <h4>Markdown Content</h4>
                        <small style={{ display: 'block', marginBottom: '0.5em', color: '#666' }}>
                            Write the static content to display using Markdown. Supports <strong>bold</strong>,
                            <em> italic</em>, lists, headings, links, and more. This block is read-only in the form.
                        </small>
                        <div className="config-field">
                            <label htmlFor="field-markdown-content">Content (Markdown)</label>
                            <textarea
                                id="field-markdown-content"
                                value={localField.content || ''}
                                onChange={(e) => handleChange('content', e.target.value)}
                                rows={6}
                                placeholder={'**Important:** Add your instructions or notes here.\n\n- Use **bold** for emphasis\n- Use _italic_ for notes\n- Use > for callout blocks'}
                                style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
                            />
                        </div>
                    </div>
                )}

                {/* Question / Text Block - styled content block with variant */}
                {localField.type === 'question-block' && (
                    <div className="config-section">
                        <h4>Question / Text Block</h4>
                        <div className="config-field">
                            <label htmlFor="field-qb-content">Block Content</label>
                            <textarea
                                id="field-qb-content"
                                value={localField.content || ''}
                                onChange={(e) => handleChange('content', e.target.value)}
                                rows={5}
                                placeholder="What is the current condition of the parking lot surface?"
                            />
                            <small className="field-hint">The text displayed inside the block. This is read-only on the form.</small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-qb-variant">Style Variant</label>
                            <select
                                id="field-qb-variant"
                                value={localField.variant || 'question'}
                                onChange={(e) => handleChange('variant', e.target.value)}
                            >
                                <option value="question">❓ Question</option>
                                <option value="instruction">📋 Instruction</option>
                                <option value="warning">⚠️ Warning</option>
                                <option value="note">📝 Note</option>
                                <option value="success">✅ Success</option>
                            </select>
                            <small className="field-hint">Controls the color and icon style of the block.</small>
                        </div>
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.showIcon !== false}
                                    onChange={(e) => handleChange('showIcon', e.target.checked)}
                                />
                                Show style icon
                            </label>
                            <small className="field-hint">Display a leading icon matching the style variant.</small>
                        </div>
                    </div>
                )}

                {/* Icon Display - decorative icon element */}
                {localField.type === 'icon-display' && (
                    <div className="config-section">
                        <h4>Icon Display</h4>
                        <div className="config-field">
                            <label>Select Icon</label>
                            <IconPicker
                                value={localField.iconClass || ''}
                                onChange={(val) => handleChange('iconClass', val)}
                                placeholder="Pick an icon..."
                            />
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-icon-size">Size</label>
                            <select
                                id="field-icon-size"
                                value={localField.iconSize || 'medium'}
                                onChange={(e) => handleChange('iconSize', e.target.value)}
                            >
                                <option value="small">Small (1rem)</option>
                                <option value="medium">Medium (1.5rem)</option>
                                <option value="large">Large (2.5rem)</option>
                                <option value="xlarge">Extra Large (4rem)</option>
                            </select>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-icon-color">Color</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <input
                                    id="field-icon-color"
                                    type="color"
                                    value={localField.iconColor || '#4a5568'}
                                    onChange={(e) => handleChange('iconColor', e.target.value)}
                                    style={{ width: '40px', height: '34px', padding: '2px', cursor: 'pointer' }}
                                />
                                <input
                                    type="text"
                                    value={localField.iconColor || '#4a5568'}
                                    onChange={(e) => handleChange('iconColor', e.target.value)}
                                    placeholder="#4a5568"
                                    style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.85em' }}
                                />
                            </div>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-icon-align">Alignment</label>
                            <select
                                id="field-icon-align"
                                value={localField.iconAlign || 'left'}
                                onChange={(e) => handleChange('iconAlign', e.target.value)}
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-icon-caption">Caption (Optional)</label>
                            <input
                                id="field-icon-caption"
                                type="text"
                                value={localField.caption || ''}
                                onChange={(e) => handleChange('caption', e.target.value)}
                                placeholder="Optional text below the icon"
                            />
                            <small className="field-hint">Short text displayed beneath the icon.</small>
                        </div>
                    </div>
                )}

                {/* Multi-Select - checkbox list with multiple selections */}
                {localField.type === 'multi-select' && (
                    <div className="config-section">
                        <h4>Multi-Select Options</h4>
                        <div className="config-field">
                            <label htmlFor="field-multi-options">Options (one per line)</label>
                            <textarea
                                id="field-multi-options"
                                value={(localField.options || []).join('\n')}
                                onChange={(e) => handleChange('options', e.target.value.split('\n').filter(o => o.trim() !== ''))}
                                rows={5}
                                placeholder={'Plumbing\nElectrical\nHVAC\nStructural\nOther'}
                            />
                            <small className="field-hint">Each line becomes a selectable checkbox option.</small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-multi-layout">Layout</label>
                            <select
                                id="field-multi-layout"
                                value={localField.layout || 'vertical'}
                                onChange={(e) => handleChange('layout', e.target.value)}
                            >
                                <option value="vertical">Vertical (stacked)</option>
                                <option value="horizontal">Horizontal (inline)</option>
                            </select>
                        </div>
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.allowOther || false}
                                    onChange={(e) => handleChange('allowOther', e.target.checked)}
                                />
                                Include &quot;Other&quot; option with text input
                            </label>
                        </div>
                    </div>
                )}

                {/* Likert Scale - agreement/satisfaction scale */}
                {localField.type === 'likert-scale' && (
                    <div className="config-section">
                        <h4>Likert Scale</h4>
                        <div className="config-field">
                            <label htmlFor="field-likert-labels">Scale Labels (one per line, low → high)</label>
                            <textarea
                                id="field-likert-labels"
                                value={(localField.scaleLabels || []).join('\n')}
                                onChange={(e) => handleChange('scaleLabels', e.target.value.split('\n').filter(l => l.trim() !== ''))}
                                rows={5}
                                placeholder={'Strongly Disagree\nDisagree\nNeutral\nAgree\nStrongly Agree'}
                            />
                            <small className="field-hint">Each line becomes a point on the scale (left to right).</small>
                        </div>
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.showLabels !== false}
                                    onChange={(e) => handleChange('showLabels', e.target.checked)}
                                />
                                Show labels below scale points
                            </label>
                        </div>
                    </div>
                )}

                {/* Image Display - static reference image */}
                {localField.type === 'image-display' && (
                    <div className="config-section">
                        <h4>Image</h4>
                        <div className="config-field">
                            <label htmlFor="field-img-src">Image URL</label>
                            <input
                                id="field-img-src"
                                type="text"
                                value={localField.imageSrc || ''}
                                onChange={(e) => handleChange('imageSrc', e.target.value)}
                                placeholder="https://example.com/image.png"
                            />
                            <small className="field-hint">Direct link to the image file (PNG, JPG, SVG).</small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-img-alt">Alt Text</label>
                            <input
                                id="field-img-alt"
                                type="text"
                                value={localField.imageAlt || ''}
                                onChange={(e) => handleChange('imageAlt', e.target.value)}
                                placeholder="Descriptive text for accessibility"
                            />
                        </div>
                        <div className="config-field-row">
                            <div className="config-field half">
                                <label htmlFor="field-img-maxw">Max Width</label>
                                <input
                                    id="field-img-maxw"
                                    type="text"
                                    value={localField.imageMaxWidth || '100%'}
                                    onChange={(e) => handleChange('imageMaxWidth', e.target.value)}
                                    placeholder="100% or 400px"
                                />
                            </div>
                            <div className="config-field half">
                                <label htmlFor="field-img-align">Alignment</label>
                                <select
                                    id="field-img-align"
                                    value={localField.imageAlign || 'left'}
                                    onChange={(e) => handleChange('imageAlign', e.target.value)}
                                >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                </select>
                            </div>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-img-caption">Caption (Optional)</label>
                            <input
                                id="field-img-caption"
                                type="text"
                                value={localField.imageCaption || ''}
                                onChange={(e) => handleChange('imageCaption', e.target.value)}
                                placeholder="Figure 1: Floor plan"
                            />
                        </div>
                    </div>
                )}

                {/* Info Alert - prominent banner */}
                {localField.type === 'info-alert' && (
                    <div className="config-section">
                        <h4>Alert Banner</h4>
                        <div className="config-field">
                            <label htmlFor="field-alert-content">Alert Message</label>
                            <textarea
                                id="field-alert-content"
                                value={localField.content || ''}
                                onChange={(e) => handleChange('content', e.target.value)}
                                rows={3}
                                placeholder="This inspection must be completed before the tenant moves in."
                            />
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-alert-variant">Alert Type</label>
                            <select
                                id="field-alert-variant"
                                value={localField.alertVariant || 'info'}
                                onChange={(e) => handleChange('alertVariant', e.target.value)}
                            >
                                <option value="info">ℹ️ Info (Blue)</option>
                                <option value="warning">⚠️ Warning (Amber)</option>
                                <option value="danger">🚨 Danger (Red)</option>
                                <option value="success">✅ Success (Green)</option>
                                <option value="neutral">📌 Neutral (Gray)</option>
                            </select>
                        </div>
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.showAlertIcon !== false}
                                    onChange={(e) => handleChange('showAlertIcon', e.target.checked)}
                                />
                                Show icon
                            </label>
                        </div>
                    </div>
                )}

                {/* Spacer field - height configuration */}
                {localField.type === 'spacer' && (
                    <div className="config-section">
                        <h4>Spacer Size</h4>
                        <div className="config-field">
                            <label htmlFor="field-height">Height (px)</label>
                            <input
                                id="field-height"
                                type="number"
                                value={parseInt(localField.height) || 20}
                                onChange={(e) => handleChange('height', `${parseInt(e.target.value) || 20}px`)}
                                min="5"
                                max="100"
                            />
                        </div>
                    </div>
                )}

                {/* Checklist Item - description field */}
                {localField.type === 'checklist-item' && (
                    <div className="config-section">
                        <h4>Checklist Item Details</h4>
                        <div className="config-field">
                            <label htmlFor="field-description">Description Text</label>
                            <textarea
                                id="field-description"
                                value={localField.description || ''}
                                onChange={(e) => handleChange('description', e.target.value)}
                                rows={3}
                                placeholder="Enter the description for this checklist item..."
                            />
                            <small className="field-hint">This text appears in the checklist row.</small>
                        </div>
                    </div>
                )}

                {/* Field Row - configuration for row layout */}
                {localField.type === 'field-row' && (
                    <div className="config-section">
                        <h4>Field Row Details</h4>
                        <p className="config-help-text">
                            A field row groups multiple fields horizontally. Use the canvas to add fields to this row.
                            Fields will be displayed side-by-side in the rendered template.
                        </p>
                        <div className="config-field">
                            <label htmlFor="field-row-label">Row Label (Optional)</label>
                            <input
                                id="field-row-label"
                                type="text"
                                value={localField.label || ''}
                                onChange={(e) => handleChange('label', e.target.value)}
                                placeholder="e.g., Contact Information"
                            />
                            <small className="field-hint">Optional label that appears above the row of fields.</small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-row-gap">Field Spacing</label>
                            <select
                                id="field-row-gap"
                                value={localField.gap || 'medium'}
                                onChange={(e) => handleChange('gap', e.target.value)}
                            >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                            </select>
                            <small className="field-hint">Spacing between fields in the row.</small>
                        </div>
                        <div className="config-field">
                            <label>Current Fields in Row</label>
                            <div className="row-fields-list">
                                {(localField.rowFields || []).length === 0 ? (
                                    <p className="no-fields-message">No fields added yet. Click the + button in the canvas to add fields.</p>
                                ) : (
                                    <ul>
                                        {(localField.rowFields || []).map((rf, idx) => {
                                            const fieldTypeDef = FIELD_TYPES.find(ft => ft.type === rf.type) || FIELD_TYPES[0];
                                            return (
                                                <li key={rf.id || idx}>
                                                    <i className={`fas ${fieldTypeDef.icon}`}></i>
                                                    {rf.label || 'Untitled'} ({fieldTypeDef.label})
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Property Selector field options */}
                {localField.type === 'property-selector' && (
                    <div className="config-section">
                        <h4>Property Selector Options</h4>
                        <p className="config-help-text">
                            This field loads properties the current user has access to.
                            Tenant and Shop Number fields can depend on this selector.
                        </p>
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.syncWithActionItemProperty !== false}
                                    onChange={(e) => handleChange('syncWithActionItemProperty', e.target.checked)}
                                />
                                Auto-sync with top-level Action Item property
                            </label>
                            <small>When enabled, this selector automatically matches the property chosen at the top of the Action Item form, removing the need for duplicate selection.</small>
                        </div>
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.showPropertyGroups || false}
                                    onChange={(e) => handleChange('showPropertyGroups', e.target.checked)}
                                />
                                Group properties by property group
                            </label>
                        </div>
                    </div>
                )}

                {/* Tenant Selector field options */}
                {localField.type === 'tenant-selector' && (
                    <div className="config-section">
                        <h4>Tenant Selector Options</h4>
                        <p className="config-help-text">
                            This field searches tenants filtered by the selected property.
                            Link it to a Property Selector field so it knows which property to filter by.
                            Configure auto-populate to fill related fields when a tenant is selected.
                        </p>
                        <div className="config-field">
                            <label htmlFor="field-dependsOn">Depends on Property Selector</label>
                            <select
                                id="field-dependsOn"
                                value={localField.dependsOnField || ''}
                                onChange={(e) => handleChange('dependsOnField', e.target.value)}
                            >
                                <option value="">-- Select Property Selector field --</option>
                                <option value="__actionItemProperty__">🏢 Action Item Property (above template)</option>
                                {allFields
                                    .filter(f => f.type === 'property-selector' && f.id !== localField.id)
                                    .map(f => (
                                        <option key={f.id} value={f.name}>
                                            {f.label || f.name}
                                        </option>
                                    ))
                                }
                            </select>
                            <small>The tenant list will be filtered by the property chosen in the linked selector. Choose &quot;Action Item Property&quot; to use the property selected above the template.</small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-minSearchLength">Minimum Search Length</label>
                            <input
                                id="field-minSearchLength"
                                type="number"
                                value={localField.minSearchLength || 2}
                                onChange={(e) => handleChange('minSearchLength', parseInt(e.target.value) || 2)}
                                min="1"
                                max="10"
                            />
                            <small>Minimum characters before tenant search triggers.</small>
                        </div>
                        <h5 style={{ marginTop: '1rem' }}>Auto-Populate Fields</h5>
                        <p className="config-help-text">
                            Map tenant data to other fields. When a tenant is selected, these fields will be automatically filled.
                        </p>
                        {['contactName', 'contactPhone', 'contactEmail', 'shopNumber', 'tenantCode', 'tradingName'].map(sourceKey => {
                            const labels = { contactName: 'Contact Name', contactPhone: 'Contact Phone', contactEmail: 'Contact Email', shopNumber: 'Shop Number', tenantCode: 'Tenant Code', tradingName: 'Trading Name' };
                            return (
                                <div className="config-field" key={sourceKey}>
                                    <label>{labels[sourceKey]} → Target Field</label>
                                    <select
                                        value={(localField.autoPopulate || {})[sourceKey] || ''}
                                        onChange={(e) => {
                                            const updated = { ...(localField.autoPopulate || {}) };
                                            if (e.target.value) {
                                                updated[sourceKey] = e.target.value;
                                            } else {
                                                delete updated[sourceKey];
                                            }
                                            handleChange('autoPopulate', Object.keys(updated).length > 0 ? updated : undefined);
                                        }}
                                    >
                                        <option value="">-- None --</option>
                                        {allFields
                                            .filter(f => f.id !== localField.id && !['property-selector', 'tenant-selector', 'shop-number', 'heading', 'divider', 'spacer', 'paragraph', 'row'].includes(f.type))
                                            .map(f => (
                                                <option key={f.id} value={f.name}>{f.label || f.name}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Shop Number field options */}
                {localField.type === 'shop-number' && (
                    <div className="config-section">
                        <h4>Shop Number Options</h4>
                        <p className="config-help-text">
                            Searchable shop/unit selector filtered by the selected property.
                            When a shop is selected, the system automatically looks up the tenant occupying that unit
                            and can auto-fill related fields.
                        </p>
                        <div className="config-field">
                            <label htmlFor="field-dependsOn-shop">Depends on Property Selector</label>
                            <select
                                id="field-dependsOn-shop"
                                value={localField.dependsOnField || ''}
                                onChange={(e) => handleChange('dependsOnField', e.target.value)}
                            >
                                <option value="">-- Select Property Selector field --</option>
                                <option value="__actionItemProperty__">🏢 Action Item Property (above template)</option>
                                {allFields
                                    .filter(f => f.type === 'property-selector' && f.id !== localField.id)
                                    .map(f => (
                                        <option key={f.id} value={f.name}>
                                            {f.label || f.name}
                                        </option>
                                    ))
                                }
                            </select>
                            <small>The shop/unit list will be filtered by the property chosen in the linked selector. Choose &quot;Action Item Property&quot; to use the property selected above the template.</small>
                        </div>
                        <h5 style={{ marginTop: '1rem' }}>Auto-Populate Fields (from tenant lookup)</h5>
                        <p className="config-help-text">
                            When a shop is selected, the system finds the current tenant and can fill these fields.
                        </p>
                        {['contactName', 'contactPhone', 'contactEmail', 'tenantName', 'tenantCode'].map(sourceKey => {
                            const labels = { contactName: 'Contact Name', contactPhone: 'Contact Phone', contactEmail: 'Contact Email', tenantName: 'Tenant Name', tenantCode: 'Tenant Code' };
                            return (
                                <div className="config-field" key={sourceKey}>
                                    <label>{labels[sourceKey]} → Target Field</label>
                                    <select
                                        value={(localField.autoPopulate || {})[sourceKey] || ''}
                                        onChange={(e) => {
                                            const updated = { ...(localField.autoPopulate || {}) };
                                            if (e.target.value) {
                                                updated[sourceKey] = e.target.value;
                                            } else {
                                                delete updated[sourceKey];
                                            }
                                            handleChange('autoPopulate', Object.keys(updated).length > 0 ? updated : undefined);
                                        }}
                                    >
                                        <option value="">-- None --</option>
                                        {allFields
                                            .filter(f => f.id !== localField.id && !['property-selector', 'tenant-selector', 'shop-number', 'heading', 'divider', 'spacer', 'paragraph', 'row'].includes(f.type))
                                            .map(f => (
                                                <option key={f.id} value={f.name}>{f.label || f.name}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Lookup Selector config */}
                {localField.type === 'lookup-selector' && (
                    <div className="config-section">
                        <h4><FaSearch /> Lookup Configuration</h4>
                        <p className="config-help-text">
                            Configure the API endpoint and field mappings for this lookup selector.
                            The endpoint must return a JSON array.
                        </p>
                        <div className="config-field">
                            <label htmlFor="field-lookupEndpoint">API Endpoint</label>
                            <input
                                id="field-lookupEndpoint"
                                type="text"
                                value={localField.lookupEndpoint || ''}
                                onChange={(e) => handleChange('lookupEndpoint', e.target.value)}
                                placeholder="e.g. /api/vendors or /api/assets"
                            />
                            <small>Relative URL to a CapTrac API that returns JSON array data.</small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-lookupValueField">Value Field</label>
                            <input
                                id="field-lookupValueField"
                                type="text"
                                value={localField.lookupValueField || ''}
                                onChange={(e) => handleChange('lookupValueField', e.target.value)}
                                placeholder="e.g. id or vendorId"
                            />
                            <small>JSON property to use as the stored value.</small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-lookupDisplayField">Display Field</label>
                            <input
                                id="field-lookupDisplayField"
                                type="text"
                                value={localField.lookupDisplayField || ''}
                                onChange={(e) => handleChange('lookupDisplayField', e.target.value)}
                                placeholder="e.g. name or displayName"
                            />
                            <small>JSON property to display in the dropdown.</small>
                        </div>
                        <div className="config-field checkbox-field">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={localField.lookupSearchable || false}
                                    onChange={(e) => handleChange('lookupSearchable', e.target.checked)}
                                />
                                Searchable (adds a type-ahead search filter)
                            </label>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-lookupDependsOn">Depends on Field (optional)</label>
                            <select
                                id="field-lookupDependsOn"
                                value={localField.lookupDependsOn || ''}
                                onChange={(e) => handleChange('lookupDependsOn', e.target.value)}
                            >
                                <option value="">-- None --</option>
                                {allFields
                                    .filter(f => f.id !== localField.id)
                                    .map(f => (
                                        <option key={f.id} value={f.name}>{f.label || f.name}</option>
                                    ))
                                }
                            </select>
                            <small>If set, the lookup will append <code>?fieldName=value</code> from the selected field.</small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-lookupQueryParam">Query Parameter Name</label>
                            <input
                                id="field-lookupQueryParam"
                                type="text"
                                value={localField.lookupQueryParam || ''}
                                onChange={(e) => handleChange('lookupQueryParam', e.target.value)}
                                placeholder="e.g. propertyId or categoryId"
                            />
                            <small>Parameter name sent with the depends-on value.</small>
                        </div>
                    </div>
                )}

                {/* Repeater config */}
                {localField.type === 'repeater' && (
                    <div className="config-section">
                        <h4><FaCubes /> Repeater Configuration</h4>
                        <p className="config-help-text">
                            Define the fields that appear in each repeating row. Users can add and remove rows at runtime.
                        </p>
                        <div className="config-field">
                            <label htmlFor="field-repeaterMinRows">Minimum Rows</label>
                            <input
                                id="field-repeaterMinRows"
                                type="number"
                                min="0"
                                value={localField.repeaterMinRows ?? 1}
                                onChange={(e) => handleChange('repeaterMinRows', parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-repeaterMaxRows">Maximum Rows (0 = unlimited)</label>
                            <input
                                id="field-repeaterMaxRows"
                                type="number"
                                min="0"
                                value={localField.repeaterMaxRows ?? 0}
                                onChange={(e) => handleChange('repeaterMaxRows', parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div className="config-field">
                            <label>Sub-Fields (one per line: name|label|type)</label>
                            <textarea
                                rows={5}
                                value={(localField.repeaterFields || []).map(sf => `${sf.name}|${sf.label}|${sf.type}`).join('\n')}
                                onChange={(e) => {
                                    const lines = e.target.value.split('\n').filter(l => l.trim());
                                    const subFields = lines.map(line => {
                                        const [name, label, type] = line.split('|').map(s => s.trim());
                                        return { name: name || '', label: label || name || '', type: type || 'text' };
                                    });
                                    handleChange('repeaterFields', subFields);
                                }}
                                placeholder={'description|Description|text\nqty|Quantity|number\nunitPrice|Unit Price|number'}
                            />
                            <small>Supported types: text, number, select, date, checkbox, textarea.</small>
                        </div>
                    </div>
                )}

                {/* Computed Value config */}
                {localField.type === 'computed-value' && (
                    <div className="config-section">
                        <h4><FaBolt /> Computed Value Expression</h4>
                        <p className="config-help-text">
                            Define an expression to calculate this field&apos;s value from other fields.
                            Reference fields by name in curly braces.
                        </p>
                        <div className="config-field">
                            <label htmlFor="field-computedExpression">Expression</label>
                            <input
                                id="field-computedExpression"
                                type="text"
                                value={localField.computedExpression || ''}
                                onChange={(e) => handleChange('computedExpression', e.target.value)}
                                placeholder="e.g. {qty} * {unitPrice}"
                            />
                            <small>
                                Operators: <code>+</code> <code>-</code> <code>*</code> <code>/</code>.
                                Concatenation: <code>{'{firstName}'} {'{lastName}'}</code>.
                            </small>
                        </div>
                        <div className="config-field">
                            <label htmlFor="field-computedFormat">Display Format</label>
                            <select
                                id="field-computedFormat"
                                value={localField.computedFormat || 'text'}
                                onChange={(e) => handleChange('computedFormat', e.target.value)}
                            >
                                <option value="text">Plain Text</option>
                                <option value="number">Number</option>
                                <option value="currency">Currency (R)</option>
                                <option value="percentage">Percentage (%)</option>
                            </select>
                        </div>
                    </div>
                )}

                {/* Conditional Logic Section - for all field types except layout-only */}
                {!['divider', 'spacer', 'heading', 'paragraph', 'markdown-block', 'row'].includes(localField.type) && allFields.length > 1 && (
                    <ConditionalLogicPanel
                        field={localField}
                        allFields={allFields}
                        onUpdate={(updatedField) => {
                            setLocalField(updatedField);
                            onUpdate(updatedField);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

FieldConfigPanel.propTypes = {
    field: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    allFields: PropTypes.array
};

/**
 * Comparison operators for conditional logic.
 */
const CONDITION_OPERATORS = [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Does not equal' },
    { value: 'contains', label: 'Contains' },
    { value: 'notContains', label: 'Does not contain' },
    { value: 'isEmpty', label: 'Is empty' },
    { value: 'isNotEmpty', label: 'Is not empty' },
    { value: 'greaterThan', label: 'Greater than' },
    { value: 'lessThan', label: 'Less than' },
    { value: 'isChecked', label: 'Is checked' },
    { value: 'isNotChecked', label: 'Is not checked' }
];

/**
 * ConditionalLogicPanel Component
 * Panel for configuring field visibility conditions.
 * Allows users to show/hide fields based on other field values.
 */
const ConditionalLogicPanel = ({ field, allFields, onUpdate }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    // Get conditions from field, default to empty array
    const conditions = field.conditions || [];
    const conditionLogic = field.conditionLogic || 'all'; // 'all' or 'any'

    // Filter out the current field and layout-only fields from available fields
    // markdown-block is also excluded as it is read-only and cannot trigger conditions
    const availableFields = allFields.filter(f => 
        f.id !== field.id && 
        !['divider', 'spacer', 'heading', 'paragraph', 'markdown-block', 'row'].includes(f.type)
    );

    const handleAddCondition = useCallback(() => {
        const newCondition = {
            id: generateId(),
            fieldId: availableFields[0]?.id || '',
            operator: 'equals',
            value: ''
        };
        onUpdate({
            ...field,
            conditions: [...conditions, newCondition]
        });
    }, [availableFields, field, conditions, onUpdate]);

    const handleUpdateCondition = (conditionId, updates) => {
        const newConditions = conditions.map(cond =>
            cond.id === conditionId ? { ...cond, ...updates } : cond
        );
        onUpdate({ ...field, conditions: newConditions });
    };

    const handleRemoveCondition = (conditionId) => {
        const newConditions = conditions.filter(cond => cond.id !== conditionId);
        onUpdate({ ...field, conditions: newConditions });
    };

    const handleToggleLogic = () => {
        onUpdate({
            ...field,
            conditionLogic: conditionLogic === 'all' ? 'any' : 'all'
        });
    };

    const handleClearConditions = () => {
        onUpdate({
            ...field,
            conditions: [],
            conditionLogic: 'all'
        });
    };

    return (
        <div className="conditional-logic-panel">
            <button
                type="button"
                className={`conditional-logic-header ${isExpanded ? 'expanded' : ''}`}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="header-content">
                    <FaFilter />
                    <span>Conditional Logic</span>
                    {conditions.length > 0 && (
                        <span className="condition-badge">{conditions.length}</span>
                    )}
                </div>
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
            </button>

            {isExpanded && (
                <div className="conditional-logic-content">
                    {availableFields.length === 0 ? (
                        <div className="no-fields-message">
                            <FaQuestionCircle />
                            <p>Add more fields to the template to create conditions.</p>
                        </div>
                    ) : (
                        <>
                            <p className="condition-intro">
                                Show this field only when the following conditions are met:
                            </p>

                            {conditions.length > 0 && (
                                <>
                                    <div className="logic-toggle">
                                        <span>Match</span>
                                        <button
                                            type="button"
                                            className={`logic-btn ${conditionLogic === 'all' ? 'active' : ''}`}
                                            onClick={handleToggleLogic}
                                        >
                                            {conditionLogic === 'all' ? 'ALL' : 'ANY'}
                                        </button>
                                        <span>of the following:</span>
                                    </div>

                                    <div className="conditions-list">
                                        {conditions.map((condition, idx) => (
                                            <div key={condition.id} className="condition-row">
                                                {idx > 0 && (
                                                    <span className="condition-connector">
                                                        {conditionLogic === 'all' ? 'AND' : 'OR'}
                                                    </span>
                                                )}
                                                <div className="condition-inputs">
                                                    <select
                                                        value={condition.fieldId}
                                                        onChange={(e) => handleUpdateCondition(condition.id, { fieldId: e.target.value })}
                                                        className="condition-field-select"
                                                    >
                                                        <option value="">Select field...</option>
                                                        {availableFields.map(af => (
                                                            <option key={af.id} value={af.id}>
                                                                {af.label || af.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <select
                                                        value={condition.operator}
                                                        onChange={(e) => handleUpdateCondition(condition.id, { operator: e.target.value })}
                                                        className="condition-operator-select"
                                                    >
                                                        {CONDITION_OPERATORS.map(op => (
                                                            <option key={op.value} value={op.value}>
                                                                {op.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {!['isEmpty', 'isNotEmpty', 'isChecked', 'isNotChecked'].includes(condition.operator) && (
                                                        <input
                                                            type="text"
                                                            value={condition.value}
                                                            onChange={(e) => handleUpdateCondition(condition.id, { value: e.target.value })}
                                                            placeholder="Value..."
                                                            className="condition-value-input"
                                                        />
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="remove-condition-btn"
                                                        onClick={() => handleRemoveCondition(condition.id)}
                                                        title="Remove condition"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            <div className="condition-actions">
                                <button
                                    type="button"
                                    className="add-condition-btn"
                                    onClick={handleAddCondition}
                                >
                                    <FaPlus /> Add Condition
                                </button>
                                {conditions.length > 0 && (
                                    <button
                                        type="button"
                                        className="clear-conditions-btn"
                                        onClick={handleClearConditions}
                                    >
                                        <FaTimes /> Clear All
                                    </button>
                                )}
                            </div>

                            {conditions.length > 0 && (
                                <div className="condition-preview">
                                    <FaEyeSlash />
                                    <span>
                                        This field will be hidden unless{' '}
                                        {conditionLogic === 'all' ? 'all' : 'any'} condition{conditions.length > 1 ? 's are' : ' is'} met.
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

ConditionalLogicPanel.propTypes = {
    field: PropTypes.object.isRequired,
    allFields: PropTypes.array.isRequired,
    onUpdate: PropTypes.func.isRequired
};

/**
 * TemplateMetadataPanel Component
 * Panel for configuring template-level metadata (name, defaults, etc.).
 * Compact professional layout with inline hints instead of verbose help blocks.
 * @param {object} metadata - Current metadata values
 * @param {function} onUpdate - Callback when metadata changes
 */
const TemplateMetadataPanel = ({ metadata, onUpdate, departmentSuggestions = [], isEditing = false }) => {
    /** When creating a new template, derive the key from the display name automatically */
    const handleDisplayNameChange = (e) => {
        const newName = e.target.value;
        const updated = { ...metadata, displayName: newName };
        if (!isEditing) {
            updated.templateKey = slugifyKey(newName);
        }
        onUpdate(updated);
    };

    return (
        <div className="template-metadata-panel redesigned">
            <div className="settings-section">
                <div className="settings-section-header">
                    <FaIdCard />
                    <span>Identity</span>
                </div>
                <div className="settings-section-body">
                    <div className="config-field">
                        <label htmlFor="template-displayName">Display Name <span className="required-mark">*</span></label>
                        <input
                            id="template-displayName"
                            type="text"
                            value={metadata.displayName || ''}
                            onChange={handleDisplayNameChange}
                            placeholder="e.g. Tenant Non-Compliance Report"
                        />
                    </div>
                    {(metadata.displayName?.trim() || isEditing) && (
                        <div className="config-field">
                            <label>System Key</label>
                            <div className="derived-key-display">
                                <code>{metadata.templateKey || '—'}</code>
                            </div>
                            <span className="field-hint">{isEditing ? 'Cannot be changed after creation' : 'Auto-generated from display name'}</span>
                        </div>
                    )}
                    <div className="config-field">
                        <label htmlFor="template-department">Department <span className="required-mark">*</span></label>
                        <select
                            id="template-department"
                            value={metadata.department || ''}
                            onChange={(e) => onUpdate({ ...metadata, department: e.target.value })}
                        >
                            <option value="">-- Select Department --</option>
                            {departmentSuggestions.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <span className="field-hint">Groups templates on management page</span>
                    </div>
                    <div className="config-field">
                        <label htmlFor="template-description">Description</label>
                        <textarea
                            id="template-description"
                            value={metadata.description || ''}
                            onChange={(e) => onUpdate({ ...metadata, description: e.target.value })}
                            rows={2}
                            placeholder="Briefly describe when this template should be used..."
                        />
                    </div>
                </div>
            </div>

            <div className="settings-section">
                <div className="settings-section-header">
                    <FaSlidersH />
                    <span>Defaults</span>
                </div>
                <div className="settings-section-body">
                    <div className="config-row-2col">
                        <div className="config-field">
                            <label htmlFor="template-defaultPriority">Priority</label>
                            <select
                                id="template-defaultPriority"
                                value={metadata.defaultPriority || 'Medium'}
                                onChange={(e) => onUpdate({ ...metadata, defaultPriority: e.target.value })}
                            >
                                {PRIORITY_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div className="config-field">
                            <label htmlFor="template-defaultStatus">Status</label>
                            <select
                                id="template-defaultStatus"
                                value={metadata.defaultStatus || 'Open'}
                                onChange={(e) => onUpdate({ ...metadata, defaultStatus: e.target.value })}
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="config-row-2col">
                        <div className="config-field">
                            <label htmlFor="template-defaultDueDays">Due Days</label>
                            <input
                                id="template-defaultDueDays"
                                type="number"
                                value={metadata.defaultDueDays || ''}
                                onChange={(e) => onUpdate({ ...metadata, defaultDueDays: e.target.value ? parseInt(e.target.value) : null })}
                                placeholder="e.g. 7"
                                min="0"
                            />
                            <span className="field-hint">Days until due date from creation</span>
                        </div>
                        <div className="config-field">
                            <label htmlFor="template-defaultBoardId">Board</label>
                            <select
                                id="template-defaultBoardId"
                                value={metadata.defaultBoardId || ''}
                                onChange={(e) => onUpdate({ ...metadata, defaultBoardId: e.target.value ? parseInt(e.target.value) : null })}
                            >
                                {(metadata.availableBoards || []).map((board) => (
                                    <option key={board.boardId} value={board.boardId}>
                                        {board.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="settings-section">
                <div className="settings-section-header">
                    <FaPalette />
                    <span>Appearance</span>
                </div>
                <div className="settings-section-body">
                    <div className="config-row-2col appearance-row">
                        <div className="config-field">
                            <label htmlFor="template-icon">Icon</label>
                            <IconPicker
                                value={metadata.icon || ''}
                                onChange={(icon) => onUpdate({ ...metadata, icon })}
                                color={metadata.color || '#3498db'}
                                placeholder="Select an icon..."
                            />
                        </div>
                        <div className="config-field">
                            <label htmlFor="template-color">Color</label>
                            <div className="color-input-group">
                                <input
                                    id="template-color"
                                    type="color"
                                    value={metadata.color || '#3498db'}
                                    onChange={(e) => onUpdate({ ...metadata, color: e.target.value })}
                                />
                                <input
                                    type="text"
                                    value={metadata.color || '#3498db'}
                                    onChange={(e) => onUpdate({ ...metadata, color: e.target.value })}
                                    placeholder="#3498db"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="config-row-2col">
                        <div className="config-field">
                            <label htmlFor="template-tags">Tags</label>
                            <input
                                id="template-tags"
                                type="text"
                                value={metadata.tags || ''}
                                onChange={(e) => onUpdate({ ...metadata, tags: e.target.value })}
                                placeholder="compliance, legal, tenant"
                            />
                            <span className="field-hint">Comma-separated for filtering</span>
                        </div>
                        <div className="config-field">
                            <label htmlFor="template-sortOrder">Display Order</label>
                            <input
                                id="template-sortOrder"
                                type="number"
                                value={metadata.sortOrder || 0}
                                onChange={(e) => onUpdate({ ...metadata, sortOrder: parseInt(e.target.value) || 0 })}
                                min="0"
                            />
                            <span className="field-hint">Lower numbers appear first</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

TemplateMetadataPanel.propTypes = {
    metadata: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired
};

/**
 * TemplatePreview Component
 * Shows a live preview of how the template will appear when rendered.
 * Includes interactive mode to test the form and a reset button.
 */
const TemplatePreview = ({ sections, metadata }) => {
    const [isInteractive, setIsInteractive] = useState(false);
    const [formData, setFormData] = useState({});

    const handleFieldChange = (fieldId, value) => {
        setFormData(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleReset = () => {
        setFormData({});
    };

    return (
        <div className="template-preview">
            <div className="preview-header">
                <h3>
                    <FaEye /> Preview
                </h3>
                <div className="preview-controls">
                    <label className="interactive-toggle">
                        <input
                            type="checkbox"
                            checked={isInteractive}
                            onChange={(e) => setIsInteractive(e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-text">Interactive Mode</span>
                    </label>
                    {isInteractive && Object.keys(formData).length > 0 && (
                        <button
                            type="button"
                            className="reset-btn"
                            onClick={handleReset}
                            title="Reset form"
                        >
                            <FaUndo /> Reset
                        </button>
                    )}
                </div>
            </div>
            <div className="preview-content">
                <div className="preview-card" style={{ borderLeftColor: metadata.color || '#3498db' }}>
                    <div className="preview-card-header">
                        {metadata.icon && <i className={`fas ${metadata.icon}`} style={{ color: metadata.color }}></i>}
                        <div className="preview-card-title">
                            <h4>{metadata.displayName || 'Untitled Template'}</h4>
                        </div>
                    </div>

                    {metadata.description && (
                        <p className="preview-description">{metadata.description}</p>
                    )}

                    <div className="preview-form">
                        {sections.map((section, sectionIdx) => (
                            <div key={section.id} className="preview-section">
                                <h5>
                                    {section.title || `Section ${sectionIdx + 1}`}
                                    {section.columns > 1 && (
                                        <span className="column-indicator">({section.columns} columns)</span>
                                    )}
                                </h5>
                                <div 
                                    className="preview-fields"
                                    style={{ 
                                        gridTemplateColumns: `repeat(${section.columns || 1}, 1fr)` 
                                    }}
                                >
                                    {section.fields.map((field) => {
                                        // Handle row type
                                        if (field.type === 'row') {
                                            const columns = field.columns || [];
                                            return (
                                                <div key={field.id} className="preview-row" style={{ gridColumn: '1 / -1' }}>
                                                    <div className="preview-row-columns" style={{ display: 'flex', gap: '1rem' }}>
                                                        {columns.map((column) => (
                                                            <div 
                                                                key={column.id} 
                                                                className="preview-row-column"
                                                                style={{ 
                                                                    flex: `0 0 ${(column.span / 12) * 100}%`,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: '0.75rem'
                                                                }}
                                                            >
                                                                {column.fields?.map((colField) => (
                                                                    <div key={colField.id} className="preview-field">
                                                                        {!hasInlineLabel(colField.type) && colField.label?.trim() && (
                                                                            <label>
                                                                                {colField.label}
                                                                                {colField.required && <span className="required">*</span>}
                                                                            </label>
                                                                        )}
                                                                        {renderPreviewInput(colField, isInteractive, formData[colField.id], (val) => handleFieldChange(colField.id, val))}
                                                                        {colField.helpText && (
                                                                            <small className="help-text">{colField.helpText}</small>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // Regular field
                                        return (
                                            <div key={field.id} className="preview-field">
                                                {!hasInlineLabel(field.type) && !isDisplayOnlyField(field.type) && field.label?.trim() && (
                                                    <label>
                                                        {field.label}
                                                        {field.required && <span className="required">*</span>}
                                                    </label>
                                                )}
                                                {renderPreviewInput(field, isInteractive, formData[field.id], (val) => handleFieldChange(field.id, val))}
                                                {field.helpText && (
                                                    <small className="help-text">{field.helpText}</small>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {section.fields.length === 0 && (
                                        <p className="no-fields">No fields in this section</p>
                                    )}
                                </div>
                            </div>
                        ))}
                        {sections.length === 0 && (
                            <p className="no-sections">Add sections and fields to see the preview</p>
                        )}
                    </div>

                    <div className="preview-defaults">
                        <span>Priority: {metadata.defaultPriority || 'Medium'}</span>
                        <span>Status: {metadata.defaultStatus || 'Open'}</span>
                        {metadata.defaultDueDays && (
                            <span>Due: {metadata.defaultDueDays} days</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Renders a preview input based on field type.
 * @param {object} field - The field definition
 * @returns {JSX.Element} The preview input element
 */
const renderPreviewInput = (field, isInteractive = false, value = '', onChange = () => {}) => {
    const placeholder = field.placeholder || `Enter ${field.label || 'value'}...`;
    const disabled = !isInteractive;

    switch (field.type) {
        case 'textarea':
            return (
                <textarea 
                    disabled={disabled} 
                    placeholder={placeholder} 
                    rows={field.rows || 3}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        case 'select':
            return (
                <select 
                    disabled={disabled}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="">-- Select --</option>
                    {(field.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            );
        case 'checkbox':
            return (
                <label className="checkbox-preview">
                    <input 
                        type="checkbox" 
                        disabled={disabled}
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                    {field.label}
                    {field.required && <span className="required">*</span>}
                </label>
            );
        case 'toggle':
            return (
                <label className="toggle-preview">
                    <input 
                        type="checkbox" 
                        disabled={disabled} 
                        className="toggle-input"
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                    <span className="toggle-slider-preview"></span>
                    <span className="toggle-label">{field.label}</span>
                    {field.required && <span className="required">*</span>}
                </label>
            );
        case 'radio':
            return (
                <div className="radio-preview">
                    <div className="radio-label">
                        {field.label}
                        {field.required && <span className="required">*</span>}
                    </div>
                    {(field.options || []).map((opt) => (
                        <label key={opt} className="radio-option">
                            <input 
                                type="radio" 
                                name={field.name} 
                                value={opt} 
                                disabled={disabled}
                                checked={value === opt}
                                onChange={(e) => onChange(e.target.value)}
                            />
                            {opt}
                        </label>
                    ))}
                </div>
            );
        case 'date':
            return (
                <input 
                    type="date" 
                    disabled={disabled}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        case 'file':
            return <input type="file" disabled={disabled} />;
        case 'number':
            return (
                <input 
                    type="number" 
                    disabled={disabled} 
                    placeholder={placeholder}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        case 'email':
            return (
                <input 
                    type="email" 
                    disabled={disabled} 
                    placeholder={placeholder}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        case 'signature':
            return (
                <div className="signature-preview-box">
                    {value ? (
                        <div className="signature-preview-signed">
                            <img src={value} alt="Signature" />
                            {isInteractive && (
                                <button
                                    type="button"
                                    className="signature-preview-clear"
                                    onClick={() => onChange('')}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="signature-preview-empty">
                            <FaPen />
                            <span>Click to sign</span>
                            <small>User's saved signature will be inserted</small>
                        </div>
                    )}
                </div>
            );
        case 'heading':
            return (
                <div className="heading-preview">
                    <h4>{field.content || field.label}</h4>
                </div>
            );
        case 'divider':
            return <hr className="divider-preview" />;
        case 'paragraph':
            return (
                <div className="paragraph-preview">
                    <p>{field.content || field.description || ''}</p>
                </div>
            );
        case 'spacer':
            return <div className="spacer-preview" style={{ height: field.height || '20px' }}></div>;
        case 'markdown-block':
            return (
                <div className="markdown-preview">
                    {field.content || <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>Markdown content...</span>}
                </div>
            );
        case 'question-block': {
            const variantStyles = {
                question: { bg: '#ebf8ff', border: '#bee3f8', icon: '❓', color: '#2b6cb0' },
                instruction: { bg: '#ebf4ff', border: '#c3dafe', icon: '📋', color: '#4338ca' },
                warning: { bg: '#fffbeb', border: '#fde68a', icon: '⚠️', color: '#b45309' },
                note: { bg: '#f0fdf4', border: '#bbf7d0', icon: '📝', color: '#15803d' },
                success: { bg: '#f0fdf4', border: '#86efac', icon: '✅', color: '#166534' }
            };
            const vs = variantStyles[field.variant] || variantStyles.question;
            return (
                <div
                    className="question-block-preview"
                    style={{
                        background: vs.bg,
                        border: `1px solid ${vs.border}`,
                        borderLeft: `4px solid ${vs.color}`,
                        borderRadius: '6px',
                        padding: '12px 14px',
                        color: vs.color,
                        fontSize: '0.9rem',
                        lineHeight: '1.5'
                    }}
                >
                    {field.showIcon !== false && <span style={{ marginRight: '8px' }}>{vs.icon}</span>}
                    {field.content || 'Question or text block content...'}
                </div>
            );
        }
        case 'icon-display': {
            const sizeMap = { small: '1rem', medium: '1.5rem', large: '2.5rem', xlarge: '4rem' };
            const iconSize = sizeMap[field.iconSize] || '1.5rem';
            const iconColor = field.iconColor || '#4a5568';
            const align = field.iconAlign || 'left';
            return (
                <div className="icon-display-preview" style={{ textAlign: align }}>
                    {field.iconClass ? (
                        <i className={`fas ${field.iconClass}`} style={{ fontSize: iconSize, color: iconColor }}></i>
                    ) : (
                        <span style={{ color: '#a0aec0', fontStyle: 'italic', fontSize: '0.85rem' }}>No icon selected</span>
                    )}
                    {field.caption && (
                        <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '4px' }}>{field.caption}</div>
                    )}
                </div>
            );
        }
        case 'multi-select': {
            const msOptions = field.options || ['Option 1', 'Option 2', 'Option 3'];
            const msValues = Array.isArray(value) ? value : [];
            const msLayout = field.layout || 'vertical';
            return (
                <div className={`multi-select-preview layout-${msLayout}`}>
                    {msOptions.map((opt, idx) => (
                        <label key={idx} className="multi-select-option">
                            <input
                                type="checkbox"
                                checked={msValues.includes(opt)}
                                onChange={(e) => {
                                    if (disabled) return;
                                    const next = e.target.checked
                                        ? [...msValues, opt]
                                        : msValues.filter(v => v !== opt);
                                    onChange(next);
                                }}
                                disabled={disabled}
                            />
                            <span>{opt}</span>
                        </label>
                    ))}
                    {field.allowOther && (
                        <label className="multi-select-option other-option">
                            <input type="checkbox" disabled={disabled} />
                            <span>Other…</span>
                        </label>
                    )}
                </div>
            );
        }
        case 'likert-scale': {
            const labels = field.scaleLabels || ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];
            const showLabels = field.showLabels !== false;
            return (
                <div className="likert-scale-preview">
                    <div className="likert-points">
                        {labels.map((lbl, idx) => (
                            <div
                                key={idx}
                                className={`likert-point${value === String(idx) ? ' selected' : ''}`}
                                onClick={() => !disabled && onChange(String(idx))}
                            >
                                <div className="likert-radio" />
                                {showLabels && <span className="likert-label">{lbl}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        case 'image-display': {
            const imgSrc = field.imageSrc || '';
            const imgAlt = field.imageAlt || 'Image';
            const imgMaxW = field.imageMaxWidth || '100%';
            const imgAlign = field.imageAlign || 'left';
            return (
                <div className="image-display-preview" style={{ textAlign: imgAlign }}>
                    {imgSrc ? (
                        <img src={imgSrc} alt={imgAlt} style={{ maxWidth: imgMaxW, borderRadius: '4px' }} />
                    ) : (
                        <div className="image-placeholder">
                            <FaImage style={{ fontSize: '2rem', color: '#cbd5e0' }} />
                            <span>No image URL set</span>
                        </div>
                    )}
                    {field.imageCaption && (
                        <div className="image-caption">{field.imageCaption}</div>
                    )}
                </div>
            );
        }
        case 'info-alert': {
            const alertStyles = {
                info:    { bg: '#ebf8ff', border: '#90cdf4', icon: 'ℹ️', color: '#2b6cb0' },
                warning: { bg: '#fffbeb', border: '#fde68a', icon: '⚠️', color: '#b45309' },
                danger:  { bg: '#fff5f5', border: '#fed7d7', icon: '🚨', color: '#c53030' },
                success: { bg: '#f0fdf4', border: '#86efac', icon: '✅', color: '#166534' },
                neutral: { bg: '#f7fafc', border: '#e2e8f0', icon: '📌', color: '#4a5568' }
            };
            const as = alertStyles[field.alertVariant] || alertStyles.info;
            return (
                <div
                    className="info-alert-preview"
                    style={{
                        background: as.bg,
                        border: `1px solid ${as.border}`,
                        borderLeft: `4px solid ${as.color}`,
                        borderRadius: '6px',
                        padding: '12px 16px',
                        color: as.color,
                        fontSize: '0.9rem',
                        lineHeight: '1.5',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px'
                    }}
                >
                    {field.showAlertIcon !== false && <span style={{ fontSize: '1.1em', flexShrink: 0 }}>{as.icon}</span>}
                    <span>{field.content || 'Alert message…'}</span>
                </div>
            );
        }
        default:
            return (
                <input 
                    type="text" 
                    disabled={disabled} 
                    placeholder={placeholder}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
    }
};

TemplatePreview.propTypes = {
    sections: PropTypes.array.isRequired,
    metadata: PropTypes.object.isRequired
};


/**
 * TemplateDesigner Component
 * Main component for designing Action Item templates with drag-and-drop functionality.
 * Allows creating, editing, and organizing fields and sections visually.
 * Note: Department field has been removed from UI - templates are linked to modules via Module Template Assignments.
 * 
 * @param {object} initialTemplate - Initial template data when editing
 * @param {function} onSave - Callback when template is saved
 * @param {function} onCancel - Callback when editing is cancelled
 * @param {number} userId - Current user's ID
 * @param {function} [onOpenWizard] - Optional callback to navigate to the Template Wizard
 */
const TemplateDesigner = ({
    initialTemplate = null,
    onSave,
    onCancel,
    userId,
    departments: departmentSuggestions = [],
    onOpenWizard,
}) => {
    const { apiFetch } = useAuth();
    // Template metadata state
    const [metadata, setMetadata] = useState({
        templateKey: initialTemplate?.templateKey || '',
        displayName: initialTemplate?.displayName || '',
        description: initialTemplate?.description || '',
        department: initialTemplate?.department || departmentSuggestions[0] || 'General',
        defaultPriority: initialTemplate?.defaultPriority || 'Medium',
        defaultStatus: initialTemplate?.defaultStatus || 'Open',
        defaultDueDays: initialTemplate?.defaultDueDays || null,
        defaultBoardId: initialTemplate?.defaultBoardId || null,
        icon: initialTemplate?.icon || '',
        color: initialTemplate?.color || '#3498db',
        tags: initialTemplate?.tags || '',
        sortOrder: initialTemplate?.sortOrder || 0
    });

    const [availableBoards, setAvailableBoards] = useState([]);
    const [, setLoadingBoards] = useState(false);

    const fetchBoards = useCallback(async () => {
        try {
            setLoadingBoards(true);
            const boardsData = await kanbanBoardService.fetchBoards(apiFetch);
            setAvailableBoards(boardsData);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to load boards:', error);
        } finally {
            setLoadingBoards(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchBoards();
    }, [fetchBoards]);

    // Parse initial schema if editing existing template
    const initialSchema = initialTemplate?.schema
        ? (typeof initialTemplate.schema === 'string' ? JSON.parse(initialTemplate.schema) : initialTemplate.schema)
        : { sections: [], fields: [], layout: { sections: [] } };

    // Convert schema to sections structure for editing
    // Supports both new format (sections with full field objects) and old format (flat fields + layout)
    const initializeSections = () => {
        /**
         * Helper to add internal ids to a field and its nested fields (for rows)
         */
        const addFieldIds = (field) => {
            const fieldWithId = { ...field, id: generateId() };
            // Handle row type fields with columns
            if (field.type === 'row' && field.columns) {
                fieldWithId.columns = field.columns.map(col => ({
                    ...col,
                    id: generateId(),
                    fields: (col.fields || []).map(colField => ({ ...colField, id: generateId() }))
                }));
            }
            return fieldWithId;
        };

        // New format: schema.sections contains full field objects
        if (initialSchema.sections?.length > 0) {
            return initialSchema.sections.map((section, idx) => ({
                id: generateId(),
                title: section.title || `Section ${idx + 1}`,
                columns: section.columns || 1,
                fields: (section.fields || []).map(addFieldIds)
            }));
        }

        // Old format: schema.layout.sections references field names from schema.fields
        if (initialSchema.layout?.sections?.length > 0) {
            return initialSchema.layout.sections.map((section, idx) => ({
                id: generateId(),
                title: section.title || `Section ${idx + 1}`,
                columns: section.columns || 1,
                fields: section.fields.map(fieldName => {
                    const fieldDef = initialSchema.fields.find(f => f.name === fieldName);
                    return fieldDef ? addFieldIds(fieldDef) : null;
                }).filter(Boolean)
            }));
        }

        // Fallback: flat fields array without layout
        if (initialSchema.fields?.length > 0) {
            return [{
                id: generateId(),
                title: 'Details',
                columns: 1,
                fields: initialSchema.fields.map(addFieldIds)
            }];
        }

        return [];
    };

    const [sections, setSections] = useState(initializeSections);
    const [selectedFieldId, setSelectedFieldId] = useState(null);
    const [selectedSectionId, setSelectedSectionId] = useState(null);
    const [activeTab, setActiveTab] = useState('fields'); // 'fields', 'settings', 'preview', 'workflows'
    const [validationErrors, setValidationErrors] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    // Workflow automation state
    const [workflows, setWorkflows] = useState([]);
    const [loadingWorkflows, setLoadingWorkflows] = useState(false);
    const [showWorkflowDesigner, setShowWorkflowDesigner] = useState(false);
    const [editingWorkflow, setEditingWorkflow] = useState(null);
    // State to track which item is being dragged (for DragOverlay)
    const [activeId, setActiveId] = useState(null);
    const [activeType, setActiveType] = useState(null); // 'section', 'field', or 'field-row'
    // Note: 'field-row' is tracked separately from 'field' to provide different visual feedback
    // in the DragOverlay, even though they share similar drag-and-drop behavior

    // Collapsible panel states for improved canvas space
    const [isPaletteCollapsed, setIsPaletteCollapsed] = useState(false);
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);

    // Load workflows when switching to the workflows tab (only for existing templates)
    const loadWorkflows = useCallback(async () => {
        const tk = metadata.templateKey || initialTemplate?.templateKey;
        if (!tk) return;
        setLoadingWorkflows(true);
        try {
            const res = await apiFetch(`/api/WorkflowAutomations?templateKey=${encodeURIComponent(tk)}`);
            const data = await res.json();
            if (data.success) setWorkflows(data.data || []);
        } catch (err) {
            Sentry.captureException(err);
        } finally {
            setLoadingWorkflows(false);
        }
    }, [apiFetch, metadata.templateKey, initialTemplate?.templateKey]);

    // ── Tasks state (checklist items defined at template level) ──
    const [taskGroups, setTaskGroups] = useState(() => {
        if (initialSchema.tasks?.length > 0) {
            return initialSchema.tasks.map(g => ({ ...g, id: generateId(), items: (g.items || []).map(i => ({ ...i, id: generateId() })) }));
        }
        return [];
    });

    const addTaskGroup = () => {
        setTaskGroups(prev => [...prev, {
            id: generateId(),
            name: `Checklist ${prev.length + 1}`,
            items: []
        }]);
    };

    const updateTaskGroup = (groupId, updates) => {
        setTaskGroups(prev => prev.map(g => g.id === groupId ? { ...g, ...updates } : g));
    };

    const removeTaskGroup = (groupId) => {
        setTaskGroups(prev => prev.filter(g => g.id !== groupId));
    };

    const addTaskItem = (groupId) => {
        setTaskGroups(prev => prev.map(g => {
            if (g.id !== groupId) return g;
            return { ...g, items: [...g.items, { id: generateId(), label: '', description: '' }] };
        }));
    };

    const updateTaskItem = (groupId, itemId, updates) => {
        setTaskGroups(prev => prev.map(g => {
            if (g.id !== groupId) return g;
            return { ...g, items: g.items.map(i => i.id === itemId ? { ...i, ...updates } : i) };
        }));
    };

    const removeTaskItem = (groupId, itemId) => {
        setTaskGroups(prev => prev.map(g => {
            if (g.id !== groupId) return g;
            return { ...g, items: g.items.filter(i => i.id !== itemId) };
        }));
    };

    useEffect(() => {
        if (activeTab === 'workflows') loadWorkflows();
    }, [activeTab, loadWorkflows]);

    // Auto-open config panel when a field is selected
    useEffect(() => {
        if (selectedFieldId) {
            setIsConfigPanelOpen(true);
        }
    }, [selectedFieldId]);

    // Undo/Redo history stack
    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isUndoRedo, setIsUndoRedo] = useState(false);
    const MAX_HISTORY = 50;

    /**
     * Push current state to history stack when sections change.
     * Skip if this change came from undo/redo operations.
     */
    const pushToHistory = useCallback((newSections) => {
        if (isUndoRedo) {
            setIsUndoRedo(false);
            return;
        }
        
        setHistory(prev => {
            // Remove any redo history when new action is taken
            const newHistory = prev.slice(0, historyIndex + 1);
            // Add new state
            newHistory.push(JSON.stringify(newSections));
            // Limit history size
            if (newHistory.length > MAX_HISTORY) {
                newHistory.shift();
                return newHistory;
            }
            return newHistory;
        });
        setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY - 1));
    }, [historyIndex, isUndoRedo]);

    /**
     * Undo the last action.
     */
    const handleUndo = useCallback(() => {
        if (historyIndex <= 0) return;
        
        setIsUndoRedo(true);
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setSections(JSON.parse(history[newIndex]));
    }, [history, historyIndex]);

    /**
     * Redo the last undone action.
     */
    const handleRedo = useCallback(() => {
        if (historyIndex >= history.length - 1) return;
        
        setIsUndoRedo(true);
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setSections(JSON.parse(history[newIndex]));
    }, [history, historyIndex]);

    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < history.length - 1;

    /**
     * Initialize history with initial state.
     */
    useEffect(() => {
        if (history.length === 0) {
            setHistory([JSON.stringify(sections)]);
            setHistoryIndex(0);
        }
    }, []); // Run only once on mount

    /**
     * Track section changes to history (debounced to avoid excessive history entries).
     */
    useEffect(() => {
        if (!isUndoRedo && history.length > 0) {
            const currentState = JSON.stringify(sections);
            if (currentState !== history[historyIndex]) {
                pushToHistory(sections);
            }
        }
    }, [sections, isUndoRedo, history, historyIndex, pushToHistory]);

    /**
     * Memoized lookup for the active item being dragged.
     * This avoids expensive recalculations on every render.
     * Both 'field' and 'field-row' types are found in section.fields,
     * but 'field-row' type shows different overlay styling.
     */
    const activeItem = useMemo(() => {
        if (!activeId) return null;
        
        if (activeType === 'section') {
            const section = sections.find(s => s.id === activeId);
            return section ? { type: 'section', data: section } : null;
        }
        
        if (activeType === 'field' || activeType === 'field-row') {
            for (const section of sections) {
                const field = section.fields.find(f => f.id === activeId);
                if (field) {
                    const fieldTypeDef = FIELD_TYPES.find(ft => ft.type === field.type);
                    return { type: activeType, data: field, typeDef: fieldTypeDef };
                }
            }
        }
        
        return null;
    }, [activeId, activeType, sections]);

    // DnD sensors with optimized settings for both section and field dragging
    // Using 10 pixel activation distance for better touch device support
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { 
                distance: 10, // Balanced distance for desktop and touch devices
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    /**
     * Get the currently selected field object.
     * Searches both section-level fields and fields inside row columns.
     */
    const selectedField = (() => {
        for (const section of sections) {
            // Check section-level fields
            const found = section.fields.find(f => f.id === selectedFieldId);
            if (found) return found;
            
            // Check fields inside row columns
            for (const field of section.fields) {
                if (field.type === 'row' && field.columns) {
                    for (const column of field.columns) {
                        const colField = column.fields?.find(cf => cf.id === selectedFieldId);
                        if (colField) {
                            // Return the field with parent info for updates
                            return { ...colField, _parentRowId: field.id, _columnId: column.id };
                        }
                    }
                }
            }
        }
        return null;
    })();

    /**
     * Adds a new section to the template.
     */
    const handleAddSection = useCallback(() => {
        const newSection = {
            id: generateId(),
            title: `Section ${sections.length + 1}`,
            columns: 1, // Default to single column
            fields: []
        };
        setSections([...sections, newSection]);
    }, [sections]);

    /**
     * Adds a new field to the selected or first section.
     * @param {string} fieldType - The type of field to add
     */
    const handleAddField = useCallback((fieldType) => {
        const fieldId = generateId();
        const newField = {
            id: fieldId,
            type: fieldType,
            name: `field_${Date.now().toString(36)}`,
            label: `New ${FIELD_TYPES.find(ft => ft.type === fieldType)?.label || 'Field'}`,
            required: false,
            placeholder: '',
            helpText: '',
            validation: {},
            options: ['select', 'radio'].includes(fieldType) ? ['Option 1', 'Option 2', 'Option 3'] : undefined,
            rowFields: fieldType === 'field-row' ? [] : undefined,
            gap: fieldType === 'field-row' ? 'medium' : undefined,
            // Question block defaults
            ...(fieldType === 'question-block' && { content: '', variant: 'question', showIcon: true }),
            // Icon display defaults
            ...(fieldType === 'icon-display' && { iconClass: '', iconSize: 'medium', iconColor: '#4a5568', iconAlign: 'left', caption: '' }),
            // Multi-select defaults
            ...(fieldType === 'multi-select' && { options: ['Option 1', 'Option 2', 'Option 3'], layout: 'vertical', allowOther: false }),
            // Likert scale defaults
            ...(fieldType === 'likert-scale' && { scaleLabels: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'], showLabels: true }),
            // Image display defaults
            ...(fieldType === 'image-display' && { imageSrc: '', imageAlt: '', imageMaxWidth: '100%', imageAlign: 'left', imageCaption: '' }),
            // Info alert defaults
            ...(fieldType === 'info-alert' && { content: '', alertVariant: 'info', showAlertIcon: true }),
            // Lookup selector defaults
            ...(fieldType === 'lookup-selector' && { lookupEndpoint: '', lookupValueField: 'id', lookupDisplayField: 'name', lookupSearchable: false }),
            // Repeater defaults
            ...(fieldType === 'repeater' && { repeaterFields: [{ name: 'item', label: 'Item', type: 'text' }], repeaterMinRows: 1, repeaterMaxRows: 0 }),
            // Computed value defaults
            ...(fieldType === 'computed-value' && { computedExpression: '', computedFormat: 'text' })
        };

        // If no sections exist, create one
        if (sections.length === 0) {
            setSections([{
                id: generateId(),
                title: 'Details',
                fields: [newField]
            }]);
        } else {
            // Add to selected section or first section
            const targetSectionId = selectedSectionId || sections[0].id;
            setSections(sections.map(section =>
                section.id === targetSectionId
                    ? { ...section, fields: [...section.fields, newField] }
                    : section
            ));
        }

        // Select the new field
        setSelectedFieldId(fieldId);
    }, [sections, selectedSectionId]);

    /**
     * Removes a field from its section.
     * @param {string} fieldId - The ID of the field to remove
     */
    const handleRemoveField = useCallback((fieldId) => {
        setSections(sections.map(section => ({
            ...section,
            fields: section.fields.filter(f => f.id !== fieldId)
        })));
        if (selectedFieldId === fieldId) {
            setSelectedFieldId(null);
        }
    }, [sections, selectedFieldId]);

    /**
     * Keyboard shortcuts handler.
     * - Ctrl/Cmd + Z: Undo
     * - Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y: Redo
     * - Delete/Backspace: Remove selected field
     * - Escape: Deselect
     */
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Skip if in an input field
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
                return;
            }

            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

            // Undo: Ctrl/Cmd + Z
            if (ctrlKey && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
                return;
            }

            // Redo: Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y
            if ((ctrlKey && e.shiftKey && e.key === 'z') || (ctrlKey && e.key === 'y')) {
                e.preventDefault();
                handleRedo();
                return;
            }

            // Delete selected field
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFieldId) {
                e.preventDefault();
                handleRemoveField(selectedFieldId);
                return;
            }

            // Escape to deselect
            if (e.key === 'Escape') {
                setSelectedFieldId(null);
                setSelectedSectionId(null);
                return;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo, selectedFieldId, handleRemoveField]);

    /**
     * Removes a section and its fields.
     * @param {string} sectionId - The ID of the section to remove
     */
    const handleRemoveSection = useCallback((sectionId) => {
        setSections(sections.filter(s => s.id !== sectionId));
        if (selectedSectionId === sectionId) {
            setSelectedSectionId(null);
        }
    }, [sections, selectedSectionId]);

    /**
     * Renames a section.
     * @param {string} sectionId - The ID of the section to rename
     * @param {string} newTitle - The new title for the section
     */
    const handleRenameSection = useCallback((sectionId, newTitle) => {
        setSections(prevSections => prevSections.map(section =>
            section.id === sectionId
                ? { ...section, title: newTitle }
                : section
        ));
    }, []);

    /**
     * Updates the number of columns for a section layout.
     * @param {string} sectionId - The ID of the section to update
     * @param {number} columns - The number of columns (1-4)
     */
    const handleUpdateColumns = useCallback((sectionId, columns) => {
        setSections(prevSections => prevSections.map(section =>
            section.id === sectionId
                ? { ...section, columns: columns }
                : section
        ));
    }, []);

    /**
     * Updates a field's properties.
     * Handles both section-level fields and fields inside row columns.
     * @param {object} updatedField - The updated field object
     */
    const handleUpdateField = useCallback((updatedField) => {
        // Check if this is a field inside a row column
        if (updatedField._parentRowId && updatedField._columnId) {
            const { _parentRowId, _columnId, ...fieldData } = updatedField;
            
            setSections(sections.map(section => ({
                ...section,
                fields: section.fields.map(f => {
                    if (f.id === _parentRowId && f.type === 'row' && f.columns) {
                        return {
                            ...f,
                            columns: f.columns.map(col => {
                                if (col.id === _columnId) {
                                    return {
                                        ...col,
                                        fields: col.fields.map(cf => 
                                            cf.id === fieldData.id ? fieldData : cf
                                        )
                                    };
                                }
                                return col;
                            })
                        };
                    }
                    return f;
                })
            })));
        } else {
            // Regular section-level field update
            setSections(sections.map(section => ({
                ...section,
                fields: section.fields.map(f =>
                    f.id === updatedField.id ? updatedField : f
                )
            })));
        }
    }, [sections]);

    /**
     * Adds a field to a field row.
     * @param {string} rowFieldId - The ID of the field-row to add to
     * @param {string} fieldType - The type of field to add
     */
    /**
     * Duplicates a field in its section.
     * @param {string} fieldId - The ID of the field to duplicate
     */
    const handleDuplicateField = useCallback((fieldId) => {
        setSections(sections.map(section => {
            const fieldIndex = section.fields.findIndex(f => f.id === fieldId);
            if (fieldIndex === -1) return section;
            
            const fieldToDuplicate = section.fields[fieldIndex];
            const duplicatedField = {
                ...fieldToDuplicate,
                id: generateId(),
                name: `${fieldToDuplicate.name}_copy_${Date.now().toString(36)}`,
                label: `${fieldToDuplicate.label} (Copy)`,
                // Deep clone rowFields if it's a field-row
                rowFields: fieldToDuplicate.rowFields ? fieldToDuplicate.rowFields.map(rf => ({
                    ...rf,
                    id: generateId(),
                    name: `${rf.name}_copy_${Date.now().toString(36)}`
                })) : undefined
            };
            
            // Insert the duplicated field right after the original
            const newFields = [...section.fields];
            newFields.splice(fieldIndex + 1, 0, duplicatedField);
            
            return { ...section, fields: newFields };
        }));
    }, [sections]);

    /**
     * Moves a field up within its section.
     * @param {string} fieldId - The ID of the field to move
     */
    const handleMoveFieldUp = useCallback((fieldId) => {
        setSections(sections.map(section => {
            const fieldIndex = section.fields.findIndex(f => f.id === fieldId);
            if (fieldIndex <= 0) return section; // Already at top or not found
            
            const newFields = [...section.fields];
            [newFields[fieldIndex - 1], newFields[fieldIndex]] = [newFields[fieldIndex], newFields[fieldIndex - 1]];
            
            return { ...section, fields: newFields };
        }));
    }, [sections]);

    /**
     * Moves a field down within its section.
     * @param {string} fieldId - The ID of the field to move
     */
    const handleMoveFieldDown = useCallback((fieldId) => {
        setSections(sections.map(section => {
            const fieldIndex = section.fields.findIndex(f => f.id === fieldId);
            if (fieldIndex === -1 || fieldIndex >= section.fields.length - 1) return section; // Not found or at bottom
            
            const newFields = [...section.fields];
            [newFields[fieldIndex], newFields[fieldIndex + 1]] = [newFields[fieldIndex + 1], newFields[fieldIndex]];
            
            return { ...section, fields: newFields };
        }));
    }, [sections]);

    /**
     * Gets the field index within its section.
     */
    const _getFieldIndex = useCallback((fieldId) => {
        for (const section of sections) {
            const idx = section.fields.findIndex(f => f.id === fieldId);
            if (idx !== -1) {
                return { index: idx, total: section.fields.length };
            }
        }
        return { index: -1, total: 0 };
    }, [sections]);

    /**
     * Handles drag start event to track the active item being dragged.
     * @param {object} event - The drag start event from DndContext
     */
    const handleDragStart = useCallback((event) => {
        const { active } = event;
        const activeData = active.data.current;
        
        setActiveId(active.id);
        setActiveType(activeData?.type || null);
    }, []);

    /**
     * Handles drag end event for reordering sections and fields.
     * Supports:
     * - Moving sections to reorder them
     * - Moving fields within the same section (reorder)
     * - Moving fields between different sections
     * - Moving field rows (treated as fields)
     * @param {object} event - The drag end event from DndContext
     */
    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        // Reset active drag state
        setActiveId(null);
        setActiveType(null);

        // If no valid drop target or dropped on itself, do nothing
        if (!over || active.id === over.id) return;

        const activeData = active.data.current;
        const overData = over.data.current;

        // Determine the type of drag operation
        const activeIsSection = activeData?.type === 'section';
        const activeIsField = activeData?.type === 'field' || activeData?.type === 'field-row' || activeData?.type === 'row';
        const overIsSection = overData?.type === 'section';
        const overIsField = overData?.type === 'field' || overData?.type === 'field-row' || overData?.type === 'row';

        // Handle section reordering (section dropped on section)
        if (activeIsSection && overIsSection) {
            setSections(prevSections => {
                const oldIndex = prevSections.findIndex(s => s.id === active.id);
                const newIndex = prevSections.findIndex(s => s.id === over.id);
                
                if (oldIndex !== -1 && newIndex !== -1) {
                    return arrayMove(prevSections, oldIndex, newIndex);
                }
                return prevSections;
            });
            return;
        }

        // Handle field and field-row reordering and movement
        if (activeIsField) {
            setSections(prevSections => {
                // Find which section the active field is in
                let sourceSectionIndex = -1;
                let sourceFieldIndex = -1;
                
                for (let i = 0; i < prevSections.length; i++) {
                    const fieldIdx = prevSections[i].fields.findIndex(f => f.id === active.id);
                    if (fieldIdx !== -1) {
                        sourceSectionIndex = i;
                        sourceFieldIndex = fieldIdx;
                        break;
                    }
                }

                if (sourceSectionIndex === -1) return prevSections;

                // Determine destination
                let destSectionIndex = -1;
                let destFieldIndex = -1;

                if (overIsField) {
                    // Dropping on another field
                    for (let i = 0; i < prevSections.length; i++) {
                        const fieldIdx = prevSections[i].fields.findIndex(f => f.id === over.id);
                        if (fieldIdx !== -1) {
                            destSectionIndex = i;
                            destFieldIndex = fieldIdx;
                            break;
                        }
                    }
                } else if (overIsSection) {
                    // Dropping on a section (add to end of that section)
                    destSectionIndex = prevSections.findIndex(s => s.id === over.id);
                    destFieldIndex = destSectionIndex !== -1 ? prevSections[destSectionIndex].fields.length : -1;
                }

                if (destSectionIndex === -1) return prevSections;

                // Same section - simple reorder
                if (sourceSectionIndex === destSectionIndex) {
                    const newSections = [...prevSections];
                    const newFields = arrayMove(
                        newSections[sourceSectionIndex].fields, 
                        sourceFieldIndex, 
                        destFieldIndex
                    );
                    newSections[sourceSectionIndex] = { 
                        ...newSections[sourceSectionIndex], 
                        fields: newFields 
                    };
                    return newSections;
                }

                // Different section - move field
                const newSections = [...prevSections];
                const fieldToMove = newSections[sourceSectionIndex].fields[sourceFieldIndex];
                
                // Remove from source
                newSections[sourceSectionIndex] = {
                    ...newSections[sourceSectionIndex],
                    fields: newSections[sourceSectionIndex].fields.filter((_, idx) => idx !== sourceFieldIndex)
                };
                
                // Add to destination
                const destFields = [...newSections[destSectionIndex].fields];
                destFields.splice(destFieldIndex, 0, fieldToMove);
                newSections[destSectionIndex] = {
                    ...newSections[destSectionIndex],
                    fields: destFields
                };

                return newSections;
            });
        }
    }, []);

    /**
     * Validates the template before saving.
     * @returns {boolean} True if valid, false otherwise
     */
    const validateTemplate = useCallback(() => {
        const errors = [];
        // Auto-derive templateKey from displayName for new templates
        if (!initialTemplate) {
            const derivedKey = slugifyKey(metadata.displayName);
            if (!derivedKey) {
                errors.push('Display Name is required (used to generate System Key)');
            } else {
                // Sync the derived key into metadata
                if (metadata.templateKey !== derivedKey) {
                    setMetadata(prev => ({ ...prev, templateKey: derivedKey }));
                }
            }
        } else if (!metadata.templateKey?.trim()) {
            errors.push('Template Key is required');
        }
        if (!metadata.displayName?.trim()) {
            errors.push('Display Name is required');
        }
        if (!metadata.department?.trim()) {
            errors.push('Department is required');
        }
        // Note: Department validation removed - templates are linked to modules via Module Template Assignments
        // The department field will use default value 'General' for backend compatibility

        // Check for duplicate field names
        const allFields = sections.flatMap(s => s.fields);
        const fieldNames = allFields.map(f => f.name);
        const duplicates = fieldNames.filter((name, idx) => fieldNames.indexOf(name) !== idx);
        if (duplicates.length > 0) {
            errors.push(`Duplicate field names found: ${[...new Set(duplicates)].join(', ')}`);
        }

        // Validate each field
        allFields.forEach((field, idx) => {
            if (!field.name?.trim()) {
                errors.push(`Field #${idx + 1} is missing a name`);
            }
            if (!field.label?.trim()) {
                errors.push(`Field "${field.name || idx + 1}" is missing a label`);
            }
        });

        setValidationErrors(errors);
        return errors.length === 0;
    }, [metadata, sections]);

    /**
     * Builds the final schema JSON from the current state.
     * @returns {object} The schema object with sections containing full field definitions
     * 
     * Output format (new sections-based structure for TemplateRenderer):
     * {
     *   sections: [
     *     {
     *       title: "Section Title",
     *       columns: 1,
     *       fields: [
     *         { name: "field1", type: "text", label: "Field 1", ... },
     *         { 
     *           type: "row", 
     *           columns: [
     *             { span: 6, fields: [{ name: "field2", type: "checkbox", ... }] },
     *             { span: 6, fields: [{ name: "field3", type: "textarea", ... }] }
     *           ]
     *         }
     *       ]
     *     }
     *   ]
     * }
     */
    const buildSchema = useCallback(() => {
        /**
         * Helper to remove internal ids from a field object
         */
        const cleanField = (field) => {
            const { id: _unusedId, ...fieldData } = field;
            return fieldData;
        };

        /**
         * Process a field, handling row types specially to preserve column structure
         */
        const processField = (field) => {
            if (field.type === 'row' && field.columns) {
                // For row fields, preserve the columns structure with nested fields
                return {
                    type: 'row',
                    name: field.name,
                    label: field.label,
                    columns: field.columns.map(col => ({
                        span: col.span || 6,
                        fields: (col.fields || []).map(colField => cleanField(colField))
                    }))
                };
            }
            // For regular fields, just remove the internal id
            return cleanField(field);
        };

        // Build sections with full field objects (new format for TemplateRenderer)
        const schemaSections = sections.map(s => ({
            title: s.title,
            columns: s.columns || 1,
            fields: s.fields.map(processField)
        }));

        // Build tasks array (strip internal ids)
        const schemaTasks = taskGroups.map(g => ({
            name: g.name,
            items: g.items.map(i => ({
                label: i.label,
                description: i.description || ''
            }))
        }));

        return { sections: schemaSections, tasks: schemaTasks };

    }, [sections, taskGroups]);

    /**
     * Handles saving the template.
     */
    const handleSave = useCallback(async () => {
        if (!validateTemplate()) {
            setActiveTab('settings');
            return;
        }
        setIsSaving(true);
        try {
            const schemaJson = JSON.stringify(buildSchema());
            const templateData = {
                templateKey: metadata.templateKey,
                displayName: metadata.displayName,
                description: metadata.description || '',
                department: metadata.department, // now user controlled
                schemaJson,
                defaultPriority: metadata.defaultPriority,
                defaultStatus: metadata.defaultStatus,
                defaultDueDays: metadata.defaultDueDays,
                defaultBoardId: metadata.defaultBoardId,
                icon: metadata.icon || null,
                color: metadata.color || null,
                tags: metadata.tags || null,
                sortOrder: metadata.sortOrder,
                userId
            };
            if (onSave) {
                await onSave(templateData, initialTemplate?.templateKey);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving template:', error);
            setValidationErrors([`Failed to save: ${error.message}`]);
        } finally {
            setIsSaving(false);
        }
    }, [validateTemplate, buildSchema, metadata, onSave, initialTemplate, userId]);

    /**
     * Export template as JSON file.
     */
    const handleExportTemplate = useCallback(() => {
        const templateData = {
            metadata: {
                templateKey: metadata.templateKey,
                displayName: metadata.displayName,
                description: metadata.description,
                department: metadata.department,
                defaultPriority: metadata.defaultPriority,
                defaultStatus: metadata.defaultStatus,
                defaultDueDays: metadata.defaultDueDays,
                icon: metadata.icon,
                color: metadata.color,
                tags: metadata.tags,
                sortOrder: metadata.sortOrder
            },
            schema: buildSchema(),
            exportedAt: new Date().toISOString(),
            version: '1.0'
        };

        const blob = new Blob([JSON.stringify(templateData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `template-${metadata.templateKey || 'untitled'}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [metadata, buildSchema]);

    /**
     * Import template from JSON file.
     */
    const handleImportTemplate = useCallback((event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const imported = JSON.parse(e.target.result);
                
                // Validate imported data structure
                if (!imported.metadata || !imported.schema) {
                    throw new Error('Invalid template file format');
                }

                // Update metadata
                setMetadata({
                    templateKey: imported.metadata.templateKey || '',
                    displayName: imported.metadata.displayName || '',
                    description: imported.metadata.description || '',
                    department: imported.metadata.department || 'General',
                    defaultPriority: imported.metadata.defaultPriority || 'Medium',
                    defaultStatus: imported.metadata.defaultStatus || 'Open',
                    defaultDueDays: imported.metadata.defaultDueDays || null,
                    icon: imported.metadata.icon || '',
                    color: imported.metadata.color || '#3498db',
                    tags: imported.metadata.tags || '',
                    sortOrder: imported.metadata.sortOrder || 0
                });

                // Convert schema to sections format
                const newSections = imported.schema.layout?.sections?.map((section, idx) => ({
                    id: generateId(),
                    title: section.title || `Section ${idx + 1}`,
                    columns: section.columns || 1,
                    fields: section.fields.map(fieldName => {
                        const fieldDef = imported.schema.fields.find(f => f.name === fieldName);
                        return fieldDef ? { ...fieldDef, id: generateId() } : null;
                    }).filter(Boolean)
                })) || [];

                setSections(newSections);
                setValidationErrors([]);
                
                // Reset file input
                event.target.value = '';
            } catch (error) {
                Sentry.captureException(error);
                console.error('Import error:', error);
                setValidationErrors([`Failed to import: ${error.message}`]);
            }
        };
        reader.readAsText(file);
    }, []);

    // Hidden file input ref for import
    const fileInputRef = React.useRef(null);

    return (
        <div className="template-designer">
            {/* Hidden file input for import */}
            <input
                type="file"
                ref={fileInputRef}
                accept=".json"
                onChange={handleImportTemplate}
                style={{ display: 'none' }}
            />

            {/* Designer Header */}
            <div className="designer-header">
                <div className="designer-header-title">
                    <h2>
                        <FaPencilRuler />
                        {initialTemplate ? 'Edit Template' : 'Create New Template'}
                    </h2>
                    {!initialTemplate && onOpenWizard && (
                        <button
                            type="button"
                            className="wizard-link-btn"
                            onClick={onOpenWizard}
                            title="Use the Template Wizard to create a template from a pre-built module preset"
                        >
                            <FaMagic /> or use Template Wizard
                        </button>
                    )}
                </div>
                
                {/* Toolbar Actions */}
                <div className="designer-toolbar">
                    {/* Undo/Redo */}
                    <div className="toolbar-group">
                        <button
                            type="button"
                            className="toolbar-btn"
                            onClick={handleUndo}
                            disabled={!canUndo}
                            title="Undo (Ctrl+Z)"
                        >
                            <FaUndo />
                        </button>
                        <button
                            type="button"
                            className="toolbar-btn"
                            onClick={handleRedo}
                            disabled={!canRedo}
                            title="Redo (Ctrl+Y)"
                        >
                            <FaRedo />
                        </button>
                    </div>

                    {/* Import/Export */}
                    <div className="toolbar-group">
                        <button
                            type="button"
                            className="toolbar-btn"
                            onClick={() => fileInputRef.current?.click()}
                            title="Import Template"
                        >
                            <FaUpload />
                        </button>
                        <button
                            type="button"
                            className="toolbar-btn"
                            onClick={handleExportTemplate}
                            title="Export Template"
                        >
                            <FaDownload />
                        </button>
                    </div>
                </div>

                <div className="designer-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onCancel}
                        disabled={isSaving}
                    >
                        <FaTimes /> Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <>
                                <FaSpinner className="fa-spin" /> Saving...
                            </>
                        ) : (
                            <>
                                <FaSave /> Save Template
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
                <div className="validation-errors">
                    <FaExclamationCircle />
                    <ul>
                        {validationErrors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Tab Navigation */}
            <div className="designer-tabs">
                <button
                    type="button"
                    className={`tab-btn ${activeTab === 'fields' ? 'active' : ''}`}
                    onClick={() => setActiveTab('fields')}
                >
                    <FaThList /> Fields
                </button>
                <button
                    type="button"
                    className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                >
                    <FaCog /> Settings
                </button>
                <button
                    type="button"
                    className={`tab-btn ${activeTab === 'preview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('preview')}
                >
                    <FaEye /> Preview
                </button>
                <button
                    type="button"
                    className={`tab-btn ${activeTab === 'workflows' ? 'active' : ''}`}
                    onClick={() => setActiveTab('workflows')}
                >
                    <FaBolt /> Workflows
                </button>
                <button
                    type="button"
                    className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tasks')}
                >
                    <FaTasks /> Tasks
                </button>
            </div>

            {/* Designer Content */}
            <div className="designer-content">
                {/* Fields Tab */}
                {activeTab === 'fields' && (
                    <div className="fields-tab">
                        {/* Kanban-style toggle tab — always visible on left edge */}
                        <button
                            type="button"
                            className="btn-toggle-palette"
                            onClick={() => setIsPaletteCollapsed(!isPaletteCollapsed)}
                            title={isPaletteCollapsed ? 'Show field palette' : 'Hide field palette'}
                            aria-label={isPaletteCollapsed ? 'Show field palette' : 'Hide field palette'}
                        >
                            <FaCubes aria-hidden="true" />
                            <span aria-hidden="true">FIELDS</span>
                        </button>

                        {/* Left Panel — slides open/closed */}
                        <div className={`designer-left-panel ${isPaletteCollapsed ? 'collapsed' : ''}`}>
                            {!isPaletteCollapsed && <FieldPalette onAddField={handleAddField} />}
                        </div>

                        <div className="designer-canvas">
                            {/* Enhanced canvas header with stats */}
                            <div className="canvas-header">
                                <div className="canvas-header-left">
                                    <h3>
                                        {metadata.displayName || 'Template Structure'}
                                    </h3>
                                    <div className="template-stats">
                                        <span className="stat-badge" title="Number of sections in this template">
                                            <FaLayerGroup /> {sections.length}
                                        </span>
                                        <span className="stat-badge" title="Total number of fields across all sections">
                                            <FaCubes /> {sections.reduce((sum, s) => sum + s.fields.length, 0)}
                                        </span>
                                        <span className="stat-badge required" title="Number of required fields that must be filled in">
                                            <FaAsterisk /> {sections.reduce((sum, s) => sum + s.fields.filter(f => f.required).length, 0)}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleAddSection}
                                >
                                    <FaPlus /> Add Section
                                </button>
                            </div>

                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                            >
                                {/* SortableContext for sections - enables section reordering */}
                                <SortableContext
                                    items={sections.map(s => s.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div 
                                        className="sections-container" 
                                        onClick={() => {
                                            setSelectedFieldId(null);
                                            setSelectedSectionId(null);
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'copy';
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const fieldType = e.dataTransfer.getData('fieldType');
                                            if (fieldType) {
                                                handleAddField(fieldType);
                                            }
                                        }}
                                    >
                                        {sections.length === 0 ? (
                                            <div className="empty-canvas enhanced">
                                                <div className="empty-canvas-icon">
                                                    <FaPencilRuler />
                                                </div>
                                                <h3>Start Building Your Template</h3>
                                                <p className="empty-canvas-description">
                                                    Add a section to organize your fields, then drag or click fields from the palette to build your form.
                                                </p>
                                                <div className="empty-canvas-steps">
                                                    <div className="onboarding-step">
                                                        <span className="step-number">1</span>
                                                        <span>Add a section to group related fields</span>
                                                    </div>
                                                    <div className="onboarding-step">
                                                        <span className="step-number">2</span>
                                                        <span>Drag fields from the left palette into sections</span>
                                                    </div>
                                                    <div className="onboarding-step">
                                                        <span className="step-number">3</span>
                                                        <span>Click any field to configure its properties</span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-lg"
                                                    onClick={handleAddSection}
                                                >
                                                    <FaPlus /> Add First Section
                                                </button>
                                            </div>
                                        ) : (
                                            sections.map((section, sectionIdx) => (
                                                <SortableSection
                                                    key={section.id}
                                                    section={section}
                                                    sectionIndex={sectionIdx}
                                                    isSelected={selectedSectionId === section.id}
                                                    onSelect={(id) => {
                                                        setSelectedSectionId(id);
                                                        setSelectedFieldId(null);
                                                    }}
                                                    onRemove={handleRemoveSection}
                                                    onRename={handleRenameSection}
                                                    onUpdateColumns={handleUpdateColumns}
                                                >
                                                    {/* SortableContext for fields within each section */}
                                                    <SortableContext
                                                        items={section.fields.map(f => f.id)}
                                                        strategy={verticalListSortingStrategy}
                                                    >
                                                        {section.fields.length === 0 ? (
                                                            <div className="empty-section enhanced">
                                                                <p>Drag fields here or quick-add:</p>
                                                                <div className="quick-add-bar">
                                                                    {[
                                                                        { type: 'text', icon: 'fa-font', label: 'Text' },
                                                                        { type: 'textarea', icon: 'fa-align-left', label: 'Text Area' },
                                                                        { type: 'select', icon: 'fa-list', label: 'Dropdown' },
                                                                        { type: 'checkbox', icon: 'fa-check-square', label: 'Checkbox' },
                                                                        { type: 'date', icon: 'fa-calendar', label: 'Date' },
                                                                        { type: 'number', icon: 'fa-hashtag', label: 'Number' },
                                                                    ].map(item => (
                                                                        <button
                                                                            key={item.type}
                                                                            type="button"
                                                                            className="quick-add-btn"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedSectionId(section.id);
                                                                                handleAddField(item.type);
                                                                            }}
                                                                            title={`Add ${item.label}`}
                                                                        >
                                                                            <i className={`fas ${item.icon}`}></i>
                                                                            <span>{item.label}</span>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            section.fields.map((field, fieldIndex) => (
                                                                field.type === 'row' ? (
                                                                    <SortableRow
                                                                        key={field.id}
                                                                        field={field}
                                                                        sectionId={section.id}
                                                                        isSelected={selectedFieldId === field.id}
                                                                        onSelect={(id) => {
                                                                            setSelectedFieldId(id);
                                                                            setSelectedSectionId(section.id);
                                                                        }}
                                                                        onRemove={handleRemoveField}
                                                                        onUpdate={handleUpdateField}
                                                                        selectedFieldId={selectedFieldId}
                                                                        onSelectField={(id) => {
                                                                            setSelectedFieldId(id);
                                                                            setSelectedSectionId(section.id);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <SortableField
                                                                        key={field.id}
                                                                        field={field}
                                                                        sectionId={section.id}
                                                                        isSelected={selectedFieldId === field.id}
                                                                        onSelect={(id) => {
                                                                            setSelectedFieldId(id);
                                                                            setSelectedSectionId(section.id);
                                                                        }}
                                                                        onRemove={handleRemoveField}
                                                                        onDuplicate={handleDuplicateField}
                                                                        onMoveUp={handleMoveFieldUp}
                                                                        onMoveDown={handleMoveFieldDown}
                                                                        canMoveUp={fieldIndex > 0}
                                                                        canMoveDown={fieldIndex < section.fields.length - 1}
                                                                    />
                                                                )
                                                            ))
                                                        )}
                                                        {/* Quick-add toolbar at bottom of section */}
                                                        {section.fields.length > 0 && (
                                                            <div className="section-quick-add" onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    type="button"
                                                                    className="quick-add-more-btn"
                                                                    onClick={() => {
                                                                        setSelectedSectionId(section.id);
                                                                        handleAddField('text');
                                                                    }}
                                                                    title="Add a text field"
                                                                >
                                                                    <FaPlus /> <span>Add Field</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </SortableContext>
                                                </SortableSection>
                                            ))
                                        )}
                                    </div>
                                </SortableContext>

                                {/* DragOverlay shows visual feedback while dragging */}
                                <DragOverlay>
                                    {activeItem?.type === 'section' ? (
                                        <div className="designer-section dragging-overlay">
                                            <div className="section-header">
                                                <div className="section-drag-handle">
                                                    <FaGripHorizontal />
                                                </div>
                                                <h4 className="section-title">
                                                    {activeItem.data.title || 'Section'}
                                                </h4>
                                            </div>
                                        </div>
                                    ) : activeItem?.type === 'field' ? (
                                        <div className="designer-field dragging-overlay">
                                            <div className="field-drag-handle">
                                                <FaGripVertical />
                                            </div>
                                            <div className="field-content">
                                                <div className="field-header">
                                                    <i className={`fas ${activeItem.typeDef?.icon || 'fa-font'}`}></i>
                                                    <span className="field-label">{activeItem.data.label || 'Untitled Field'}</span>
                                                </div>
                                                <div className="field-meta">
                                                    <span className="field-type-badge">{activeItem.typeDef?.label || 'Field'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                    
                                </DragOverlay>
                            </DndContext>
                        </div>

                        {/* Slide-over Config Panel */}
                        {isConfigPanelOpen && (
                            <div className="config-panel-backdrop" onClick={() => { setIsConfigPanelOpen(false); setSelectedFieldId(null); }} />
                        )}
                        <div className={`designer-right-panel slide-over ${isConfigPanelOpen && selectedField ? 'open' : ''}`}>
                            {selectedField ? (
                                <FieldConfigPanel
                                    field={selectedField}
                                    onUpdate={handleUpdateField}
                                    onClose={() => { setSelectedFieldId(null); setIsConfigPanelOpen(false); }}
                                    allFields={sections.flatMap(s => {
                                        const result = [];
                                        s.fields.forEach(f => {
                                            result.push(f);
                                            if (f.type === 'row' && f.columns) {
                                                f.columns.forEach(col => {
                                                    if (col.fields) result.push(...col.fields);
                                                });
                                            }
                                        });
                                        return result;
                                    })}
                                />
                            ) : (
                                <div className="no-selection-panel">
                                    <FaHandPointer />
                                    <p>Select a field to configure its properties</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="settings-tab">
                        <TemplateMetadataPanel
                            metadata={{
                                ...metadata,
                                availableBoards: availableBoards  
                            }}
                            onUpdate={setMetadata}
                            departmentSuggestions={departmentSuggestions}
                            isEditing={!!initialTemplate}
                        />
                    </div>
                )}

                {/* Preview Tab */}
                {activeTab === 'preview' && (
                    <div className="preview-tab">
                        <TemplatePreview
                            sections={sections}
                            metadata={metadata}
                        />
                    </div>
                )}

                {/* Workflows Tab */}
                {activeTab === 'workflows' && (
                    <div className="workflows-tab" style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#2d3748' }}>Workflow Automations</h3>
                                <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#718096' }}>
                                    Define automated actions that run when specific conditions are met on action items using this template.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => { setEditingWorkflow(null); setShowWorkflowDesigner(true); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#F63040', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                                disabled={!metadata.templateKey}
                            >
                                <FaPlus /> New Workflow
                            </button>
                        </div>

                        {!metadata.templateKey && (
                            <div style={{ padding: '16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', color: '#b45309', fontSize: '0.85rem' }}>
                                <FaExclamationTriangle style={{ marginRight: '8px' }} />
                                Save the template first to add workflow automations. The template key is required.
                            </div>
                        )}

                        {loadingWorkflows && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', padding: '40px', color: '#718096' }}>
                                <FaSpinner className="spinner" /> Loading workflows...
                            </div>
                        )}

                        {!loadingWorkflows && workflows.length === 0 && metadata.templateKey && (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#a0aec0' }}>
                                <FaBolt style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.4 }} />
                                <p style={{ margin: 0, fontWeight: 600, color: '#718096' }}>No workflows configured</p>
                                <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
                                    Create your first workflow to automate actions like disabling accounts, sending notifications, or cleaning up emails.
                                </p>
                            </div>
                        )}

                        {workflows.map(wf => (
                            <div
                                key={wf.id}
                                style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    padding: '14px 18px',
                                    marginBottom: '10px',
                                    background: wf.isActive ? '#fff' : '#f7fafc',
                                    opacity: wf.isActive ? 1 : 0.7,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    transition: 'box-shadow 0.2s'
                                }}
                                onClick={() => { setEditingWorkflow(wf); setShowWorkflowDesigner(true); }}
                                onMouseOver={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                                onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}
                            >
                                <div>
                                    <div style={{ fontWeight: 600, color: '#2d3748', fontSize: '0.9rem' }}>
                                        <FaBolt style={{ color: '#F63040', marginRight: '8px' }} />
                                        {wf.name}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', color: '#718096', marginTop: '2px' }}>
                                        Trigger: {wf.triggerType} · {wf.steps?.length || 0} step{(wf.steps?.length || 0) !== 1 ? 's' : ''}
                                    </div>
                                    {wf.description && (
                                        <div style={{ fontSize: '0.75rem', color: '#a0aec0', marginTop: '2px' }}>{wf.description}</div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{
                                        fontSize: '0.72rem',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        background: wf.isActive ? '#f0fff4' : '#f7fafc',
                                        color: wf.isActive ? '#38a169' : '#a0aec0',
                                        fontWeight: 600
                                    }}>
                                        {wf.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        ))}

                        {/* Workflow Designer Modal */}
                        {showWorkflowDesigner && (
                            <WorkflowDesigner
                                templateKey={metadata.templateKey}
                                templateSchema={JSON.stringify(buildSchema())}
                                existingWorkflow={editingWorkflow}
                                onClose={(saved) => {
                                    setShowWorkflowDesigner(false);
                                    setEditingWorkflow(null);
                                    if (saved) loadWorkflows();
                                }}
                            />
                        )}
                    </div>
                )}

                {/* Tasks Tab */}
                {activeTab === 'tasks' && (
                    <div className="tasks-tab" style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#2d3748' }}>Task Checklists</h3>
                                <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#718096' }}>
                                    Define checklist groups with items. Action items using this template will show these as a Tasks tab.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={addTaskGroup}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#F63040', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                            >
                                <FaPlus /> Add Checklist
                            </button>
                        </div>

                        {taskGroups.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#a0aec0' }}>
                                <FaTasks style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.4 }} />
                                <p style={{ margin: 0, fontWeight: 600, color: '#718096' }}>No task checklists defined</p>
                                <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
                                    Add a checklist to define tasks that users must complete on action items.
                                </p>
                            </div>
                        )}

                        {taskGroups.map((group) => (
                            <div key={group.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '16px', background: '#fff' }}>
                                {/* Group header */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f7fafc', borderRadius: '8px 8px 0 0' }}>
                                    <FaLayerGroup style={{ color: '#8b6e5a' }} />
                                    <input
                                        type="text"
                                        value={group.name}
                                        onChange={(e) => updateTaskGroup(group.id, { name: e.target.value })}
                                        style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '4px', padding: '6px 10px', fontSize: '0.9rem', fontWeight: 600 }}
                                        placeholder="Checklist name..."
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeTaskGroup(group.id)}
                                        title="Remove checklist"
                                        style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', padding: '4px' }}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>

                                {/* Items */}
                                <div style={{ padding: '12px 16px' }}>
                                    {group.items.map((item, idx) => (
                                        <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px', padding: '8px', background: '#f7fafc', borderRadius: '6px', border: '1px solid #edf2f7' }}>
                                            <span style={{ color: '#a0aec0', fontWeight: 600, minWidth: '24px', paddingTop: '6px' }}>{idx + 1}.</span>
                                            <div style={{ flex: 1 }}>
                                                <input
                                                    type="text"
                                                    value={item.label}
                                                    onChange={(e) => updateTaskItem(group.id, item.id, { label: e.target.value })}
                                                    placeholder="Task item label..."
                                                    style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '6px 10px', fontSize: '0.85rem', marginBottom: '4px' }}
                                                />
                                                <input
                                                    type="text"
                                                    value={item.description || ''}
                                                    onChange={(e) => updateTaskItem(group.id, item.id, { description: e.target.value })}
                                                    placeholder="Description (optional)..."
                                                    style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4px 10px', fontSize: '0.8rem', color: '#718096' }}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeTaskItem(group.id, item.id)}
                                                title="Remove item"
                                                style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', padding: '4px', marginTop: '4px' }}
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                    ))}

                                    <button
                                        type="button"
                                        onClick={() => addTaskItem(group.id)}
                                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1px dashed #cbd5e0', borderRadius: '6px', padding: '8px 14px', color: '#718096', cursor: 'pointer', fontSize: '0.82rem', width: '100%', justifyContent: 'center' }}
                                    >
                                        <FaPlus /> Add Item
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

TemplateDesigner.propTypes = {
    initialTemplate: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    userId: PropTypes.number.isRequired
};

export default TemplateDesigner;
